import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoChevronDown, IoSearch, IoNotifications, IoMail, IoSave, IoPerson, IoDocumentText, IoOptions, IoPersonCircle, IoCard, IoNotificationsOutline, IoShieldHalf, IoPlayCircle, IoInformationCircleOutline } from 'react-icons/io5';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/features/auth/context/AuthContext';
import { useUserProfile } from '@/hooks/useUserProfile';
import CommonAppModal from '@/components/CommonAppModal';
import FamilyDescriptionBox from '@/components/FamilyDescriptionBox';
import FamilyDataDisplay from '@/components/FamilyDataDisplay';
import MissingInfoPrompts from '@/components/MissingInfoPrompts';
import ActivityDescriptionBox from '@/components/ActivityDescriptionBox';
import ActivityCardsDisplay from '@/components/ActivityCardsDisplay';
import { useFamilyData } from '@/hooks/useFamilyData';
import { useActivityData } from '@/hooks/useActivityData';
import { useEducationData } from '@/hooks/useEducationData';
import EducationModule from '@/components/EducationModule';
import CareerModule from '@/components/CareerModule';
import ToastService from '@/utils/toast';
import './Settings.css';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { startTour } = useAuth();
  const { profile, loading, error, updateProfile } = useUserProfile();
  const { familyData, missingInfo, isLoading: familyLoading } = useFamilyData();
  const { 
    activities, 
    missingInfo: activityMissingInfo, 
    isLoading: activitiesLoading,
    analyzeActivity,
    createActivity,
    updateActivity,
    deleteActivity,
    reorderActivities,
    isAnalyzing
  } = useActivityData();
  const { 
    schools, 
    isLoading: educationLoading 
  } = useEducationData();
  const [activeTab, setActiveTab] = useState<'profile' | 'general' | 'billing' | 'notifications'>('profile');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'profile-personal': true,
    'family': false,
    'education': true,
    'testing': false,
    'activities': false,
    'career': false,
    'preferences': false,
    'appearance': true,
    'dashboard-preferences': true,
    'accessibility': true,
  });

  // Profile form toggles
  const [hasPreferredName, setHasPreferredName] = useState(false);
  const [hasFormerLegalName, setHasFormerLegalName] = useState(false);
  const [hasSeparateMailingAddress, setHasSeparateMailingAddress] = useState(false);
  const [hasOtherGender, setHasOtherGender] = useState(false);
  const [hasOtherPronouns, setHasOtherPronouns] = useState(false);
  const [selectedGender, setSelectedGender] = useState('');
  const [selectedPronouns, setSelectedPronouns] = useState('');
  
  // Address state
  const [homeAddress, setHomeAddress] = useState({
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'United States'
  });
  const [mailingAddress, setMailingAddress] = useState({
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'United States'
  });
  const [isEditingHomeAddress, setIsEditingHomeAddress] = useState(false);
  const [isEditingMailingAddress, setIsEditingMailingAddress] = useState(false);
  
  // Phone validation state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');
  
  // Military service state
  const [militaryStatus, setMilitaryStatus] = useState('');
  const [showMilitaryEnrollmentStatus, setShowMilitaryEnrollmentStatus] = useState(false);
  
  // Ethnicity state
  const [selectedEthnicities, setSelectedEthnicities] = useState<string[]>([]);
  const [showLatinoNationality, setShowLatinoNationality] = useState(false);
  
  // Language state
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [languageProficiencies, setLanguageProficiencies] = useState<Record<string, string[]>>({});

  // Citizenship info tooltip state
  const [showCitizenshipTooltip, setShowCitizenshipTooltip] = useState(false);
  const [citizenshipStatus, setCitizenshipStatus] = useState('');

  // Common App integration state
  const [showCommonAppModal, setShowCommonAppModal] = useState(false);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Family module state
  const [showFamilyResults, setShowFamilyResults] = useState(false);

  // Activity module state
  const [showActivityResults, setShowActivityResults] = useState(false);

  // General settings state - Initialize with localStorage values to prevent reset
  const [selectedTimezone, setSelectedTimezone] = useState(() => {
    return localStorage.getItem('selectedTimezone') || 'Eastern Time (ET)';
  });
  const [selectedDateFormat, setSelectedDateFormat] = useState(() => {
    return localStorage.getItem('selectedDateFormat') || 'MM/DD/YYYY';
  });
  const [textSize, setTextSize] = useState(() => {
    const saved = localStorage.getItem('textSize');
    return saved ? parseInt(saved) : 3; // 1-5 scale, 3 is medium
  });
  const [highContrastMode, setHighContrastMode] = useState(() => {
    return localStorage.getItem('highContrastMode') === 'true';
  });
  const [screenReaderOptimized, setScreenReaderOptimized] = useState(() => {
    return localStorage.getItem('screenReaderOptimized') === 'true';
  });
  const [reducedMotion, setReducedMotion] = useState(() => {
    return localStorage.getItem('reducedMotion') === 'true';
  });



  // Testing section state
  const [standardizedTest, setStandardizedTest] = useState('');
  const [advancedCourses, setAdvancedCourses] = useState<string[]>([]);
  const [languageTest, setLanguageTest] = useState('');
  const [showLanguageTest, setShowLanguageTest] = useState(true); // Default to true if fields are empty
  
  // TOEFL iBT scores state
  const [toeflScores, setToeflScores] = useState({
    total: { score: '', date: '' },
    reading: { score: '', date: '' },
    writing: { score: '', date: '' },
    speaking: { score: '', date: '' },
    listening: { score: '', date: '' }
  });
  
  // Duolingo English Test scores state
  const [duolingoScores, setDuolingoScores] = useState({
    total: { score: '', date: '' },
    literacy: { score: '', date: '' },
    comprehension: { score: '', date: '' },
    conversation: { score: '', date: '' },
    production: { score: '', date: '' }
  });
  
  // IELTS scores state
  const [ieltsScores, setIeltsScores] = useState({
    overall: { score: '', date: '' },
    reading: { score: '', date: '' },
    writing: { score: '', date: '' },
    listening: { score: '', date: '' },
    speaking: { score: '', date: '' }
  });
  
  // PTE Academic Test scores state
  const [pteScores, setPteScores] = useState({
    listening: { score: '', date: '' },
    reading: { score: '', date: '' },
    speaking: { score: '', date: '' },
    writing: { score: '', date: '' },
    grammar: { score: '', date: '' },
    oral_fluency: { score: '', date: '' },
    pronunciation: { score: '', date: '' },
    spelling: { score: '', date: '' },
    vocabulary: { score: '', date: '' },
    written_discourse: { score: '', date: '' }
  });
  
  // Cambridge English Qualification state
  const [cambridgeQualification, setCambridgeQualification] = useState({
    proficiency_level: '',
    exam_date: ''
  });
  
  // ACT scores state
  const [actScores, setActScores] = useState({
    composite: { score: '', date: '' },
    math: { score: '', date: '' },
    english: { score: '', date: '' },
    science: { score: '', date: '' },
    reading: { score: '', date: '' }
  });
  const [submitWritingScore, setSubmitWritingScore] = useState(false);
  const [writingScore, setWritingScore] = useState({ score: '', date: '' });

  // SAT scores state
  const [satScores, setSatScores] = useState({
    total: { score: '', date: '' },
    readingWriting: { score: '', date: '' },
    math: { score: '', date: '' },
    essay: { score: '', date: '' }
  });
  
  // Advanced course details state
  const [apCount, setApCount] = useState(0);
  const [ibCount, setIbCount] = useState(0);
  const [satSubjectCount, setSatSubjectCount] = useState(0);
  const [cambridgeCount, setCambridgeCount] = useState(0);
  
  // Individual test details
  const [apTests, setApTests] = useState<Array<{subject: string, dateTaken: string, score: string}>>([]);
  const [ibTests, setIbTests] = useState<Array<{subject: string, dateTaken: string, score: string, level: string}>>([]);
  const [satSubjectTests, setSatSubjectTests] = useState<Array<{subject: string, dateTaken: string, score: string}>>([]);
  const [cambridgeTests, setCambridgeTests] = useState<Array<{subject: string, dateTaken: string, score: string, level: string}>>([]);

  // Handle advanced course checkbox changes
  const handleAdvancedCourseChange = (courseType: string, checked: boolean) => {
    if (checked) {
      setAdvancedCourses([...advancedCourses, courseType]);
    } else {
      setAdvancedCourses(advancedCourses.filter(course => course !== courseType));
      // Reset count and tests when unchecked
      switch (courseType) {
        case 'AP':
          setApCount(0);
          setApTests([]);
          break;
        case 'IB':
          setIbCount(0);
          setIbTests([]);
          break;
        case 'SAT':
          setSatSubjectCount(0);
          setSatSubjectTests([]);
          break;
        case 'Cambridge':
          setCambridgeCount(0);
          setCambridgeTests([]);
          break;
      }
    }
  };

  // Handle count changes and update test arrays
  const handleCountChange = (courseType: string, count: number) => {
    switch (courseType) {
      case 'AP':
        setApCount(count);
        const newApTests = Array.from({ length: count }, (_, i) => 
          apTests[i] || { subject: '', dateTaken: '', score: '' }
        );
        setApTests(newApTests);
        break;
      case 'IB':
        setIbCount(count);
        const newIbTests = Array.from({ length: count }, (_, i) => 
          ibTests[i] || { subject: '', dateTaken: '', score: '', level: '' }
        );
        setIbTests(newIbTests);
        break;
      case 'SAT':
        setSatSubjectCount(count);
        const newSatTests = Array.from({ length: count }, (_, i) => 
          satSubjectTests[i] || { subject: '', dateTaken: '', score: '' }
        );
        setSatSubjectTests(newSatTests);
        break;
      case 'Cambridge':
        setCambridgeCount(count);
        const newCambridgeTests = Array.from({ length: count }, (_, i) => 
          cambridgeTests[i] || { subject: '', dateTaken: '', score: '', level: '' }
        );
        setCambridgeTests(newCambridgeTests);
        break;
    }
  };

  // Get Cambridge score options based on course level
  const getCambridgeScoreOptions = (level: string) => {
    const baseOptions = ['A', 'B', 'C', 'D', 'E'];
    
    if (level === 'Advanced (A-Level)' || level === 'Pre-Advanced (IGCSE)') {
      return ['A*', ...baseOptions, ...(level === 'Pre-Advanced (IGCSE)' ? ['F', 'G'] : [])];
    } else if (level === 'Advanced (AS-Level)') {
      return baseOptions;
    }
    
    return baseOptions;
  };

  // Update individual test details
  const updateTestDetail = (courseType: string, index: number, field: string, value: string) => {
    switch (courseType) {
      case 'AP':
        const updatedApTests = [...apTests];
        updatedApTests[index] = { ...updatedApTests[index], [field]: value };
        setApTests(updatedApTests);
        break;
      case 'IB':
        const updatedIbTests = [...ibTests];
        updatedIbTests[index] = { ...updatedIbTests[index], [field]: value };
        setIbTests(updatedIbTests);
        break;
      case 'SAT':
        const updatedSatTests = [...satSubjectTests];
        updatedSatTests[index] = { ...updatedSatTests[index], [field]: value };
        setSatSubjectTests(updatedSatTests);
        break;
      case 'Cambridge':
        const updatedCambridgeTests = [...cambridgeTests];
        updatedCambridgeTests[index] = { ...updatedCambridgeTests[index], [field]: value };
        // Reset score if level changes to ensure valid score for new level
        if (field === 'level') {
          updatedCambridgeTests[index].score = '';
        }
        setCambridgeTests(updatedCambridgeTests);
        break;
    }
  };

  // Check if family data exists to determine initial view state
  useEffect(() => {
    if (familyData && Object.keys(familyData).length > 0) {
      setShowFamilyResults(true);
    }
  }, [familyData]);

  // Check if activities exist to determine initial view state
  useEffect(() => {
    if (activities && activities.length > 0) {
      setShowActivityResults(true);
    }
  }, [activities]);

  // Check if schools exist to determine initial view state


  // Auto-load birthdate from onboarding data if profile doesn't have it
  useEffect(() => {
    if (profile && !profile.date_of_birth) {
      // Check for onboarding data in localStorage
      const onboardingData = localStorage.getItem('user_onboarding_data');
      if (onboardingData) {
        try {
          const parsedData = JSON.parse(onboardingData);
          if (parsedData.date_of_birth) {
            // Update profile with birthdate from onboarding
            updateProfile({ date_of_birth: parsedData.date_of_birth });
          }
        } catch (error) {
          console.warn('Failed to parse onboarding data:', error);
        }
      }
    }
  }, [profile, updateProfile]);

  // Populate form when profile data loads
  useEffect(() => {
    if (profile) {
      // Basic info
      setSelectedGender(profile.gender || '');
      setSelectedPronouns(profile.pronouns || '');
      setPhoneNumber(profile.phone_number || '');
      setMilitaryStatus(profile.military_status || '');
      setCitizenshipStatus(profile.citizenship_status || '');
      setLanguageTest(profile.language_test || '');
      
      // TOEFL scores
      if (profile.toefl_scores) {
        setToeflScores(profile.toefl_scores);
      }
      
      // Duolingo scores
      if (profile.duolingo_scores) {
        setDuolingoScores(profile.duolingo_scores);
      }
      
      // IELTS scores
      if (profile.ielts_scores) {
        setIeltsScores(profile.ielts_scores);
      }
      
      // PTE scores
      if (profile.pte_scores) {
        setPteScores(profile.pte_scores);
      }
      
      // Cambridge qualification
      if (profile.cambridge_qualification) {
        setCambridgeQualification(profile.cambridge_qualification);
      }
      
      // Address
      setHomeAddress({
        street: profile.home_street || '',
        city: profile.home_city || '',
        state: profile.home_state || '',
        postalCode: profile.home_postal_code || '',
        country: profile.home_country || 'United States'
      });
      
      // Toggles
      setHasPreferredName(!!profile.preferred_name);
      setHasFormerLegalName(!!profile.former_legal_name);
      setHasSeparateMailingAddress(!!profile.has_separate_mailing_address);
      
      // Languages
      if (profile.other_languages) {
        setSelectedLanguages(profile.other_languages);
      }
      if (profile.language_proficiencies) {
        setLanguageProficiencies(profile.language_proficiencies);
      }
      
      // Ethnicities
      if (profile.ethnicities) {
        const ethnicityKeys = Object.keys(profile.ethnicities).filter(key => profile.ethnicities![key]);
        setSelectedEthnicities(ethnicityKeys);
        setShowLatinoNationality(ethnicityKeys.includes('Latino/a/x'));
      }
    }
  }, [profile]);

  // Auto-detect if English proficiency test is needed based on language responses
  React.useEffect(() => {
    // If no language data exists, show language test by default
    if (!selectedLanguages.length || !languageProficiencies) {
      setShowLanguageTest(true);
      return;
    }

    // Check if English is first language or if user has high English proficiency
    const englishProficiency = languageProficiencies['English'] || [];
    const isEnglishFirstLanguage = englishProficiency.includes('First Language');
    const hasHighEnglishProficiency = englishProficiency.includes('Speak') && 
                                     englishProficiency.includes('Read') && 
                                     englishProficiency.includes('Write');

    // Show language test if English is not first language and doesn't have high proficiency
    setShowLanguageTest(!isEnglishFirstLanguage && !hasHighEnglishProficiency);
  }, [selectedLanguages, languageProficiencies]);

  // Apply DOM changes based on initial state values
  React.useEffect(() => {
    // Apply text size to DOM
    document.documentElement.style.fontSize = `${0.8 + (textSize - 1) * 0.1}rem`;

    // Apply accessibility classes to DOM
    if (highContrastMode) {
      document.documentElement.classList.add('high-contrast');
    }

    if (screenReaderOptimized) {
      document.documentElement.classList.add('screen-reader-optimized');
    }

    if (reducedMotion) {
      document.documentElement.classList.add('reduce-motion');
    }
  }, []); // Only run once on mount since state is initialized correctly

  // Save accessibility settings to localStorage and update DOM when they change
  React.useEffect(() => {
    localStorage.setItem('textSize', textSize.toString());
    document.documentElement.style.fontSize = `${0.8 + (textSize - 1) * 0.1}rem`;
  }, [textSize]);

  React.useEffect(() => {
    localStorage.setItem('highContrastMode', highContrastMode.toString());
    if (highContrastMode) {
      document.documentElement.classList.add('high-contrast');
    } else {
      document.documentElement.classList.remove('high-contrast');
    }
  }, [highContrastMode]);

  React.useEffect(() => {
    localStorage.setItem('screenReaderOptimized', screenReaderOptimized.toString());
    if (screenReaderOptimized) {
      document.documentElement.classList.add('screen-reader-optimized');
    } else {
      document.documentElement.classList.remove('screen-reader-optimized');
    }
  }, [screenReaderOptimized]);

  React.useEffect(() => {
    localStorage.setItem('reducedMotion', reducedMotion.toString());
    if (reducedMotion) {
      document.documentElement.classList.add('reduce-motion');
    } else {
      document.documentElement.classList.remove('reduce-motion');
    }
  }, [reducedMotion]);

  React.useEffect(() => {
    localStorage.setItem('selectedTimezone', selectedTimezone);
  }, [selectedTimezone]);

  React.useEffect(() => {
    localStorage.setItem('selectedDateFormat', selectedDateFormat);
  }, [selectedDateFormat]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleThemeChange = (newTheme: 'Light' | 'Dark') => {
    setTheme(newTheme);
    // TODO: Save theme preference to backend API
  };

  // Save profile changes
  const handleSaveProfile = async () => {
    try {
      setIsSaving(true);
      await updateProfile({
        citizenship_status: citizenshipStatus,
        gender: selectedGender,
        pronouns: selectedPronouns,
        phone_number: phoneNumber,
        military_status: militaryStatus,
        language_test: languageTest,
        toefl_scores: languageTest === 'TOEFL' ? toeflScores : undefined,
        duolingo_scores: languageTest === 'Duolingo' ? duolingoScores : undefined,
        ielts_scores: languageTest === 'IELTS' ? ieltsScores : undefined,
        pte_scores: languageTest === 'PTE' ? pteScores : undefined,
        cambridge_qualification: languageTest === 'Cambridge' ? cambridgeQualification : undefined,
        // Add other fields as needed
      });
      setHasUnsavedChanges(false);
      ToastService.showSuccess('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to save profile:', error);
      ToastService.showError('Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Track changes to citizenship status
  const handleCitizenshipStatusChange = (value: string) => {
    setCitizenshipStatus(value);
    setHasUnsavedChanges(true);
  };

  // Track changes to other profile fields
  const handleGenderChange = (value: string) => {
    setSelectedGender(value);
    setHasUnsavedChanges(true);
  };

  const handlePronounsChange = (value: string) => {
    setSelectedPronouns(value);
    setHasUnsavedChanges(true);
  };

  const handleMilitaryStatusChange = (value: string) => {
    setMilitaryStatus(value);
    setHasUnsavedChanges(true);
  };

  const handleLanguageTestChange = (value: string) => {
    setLanguageTest(value);
    setHasUnsavedChanges(true);
  };

  // Address formatting and country utilities
  const formatAddress = (address: typeof homeAddress) => {
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.postalCode) parts.push(address.postalCode);
    if (address.country && address.country !== 'United States') parts.push(address.country);
    return parts.join(', ') || 'No address entered';
  };

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'Italy', 'Spain', 'Netherlands', 'Sweden', 'Norway', 'Denmark', 'Finland', 'Switzerland', 'Austria', 'Belgium', 'Ireland', 'New Zealand', 'Japan', 'South Korea', 'Singapore', 'Other'
  ];

  const getStateLabel = (country: string) => {
    switch (country) {
      case 'United States': return 'State';
      case 'Canada': return 'Province';
      case 'United Kingdom': return 'County';
      case 'Australia': return 'State/Territory';
      default: return 'State/Province';
    }
  };

  const getPostalCodeLabel = (country: string) => {
    switch (country) {
      case 'United States': return 'ZIP Code';
      case 'Canada': return 'Postal Code';
      case 'United Kingdom': return 'Postcode';
      default: return 'Postal Code';
    }
  };

  // Comprehensive language list
  const allLanguages = [
    'English', 'Spanish', 'Mandarin Chinese', 'Hindi', 'Arabic', 'Portuguese', 'Bengali', 'Russian', 'Japanese', 'French',
    'German', 'Korean', 'Italian', 'Vietnamese', 'Turkish', 'Polish', 'Dutch', 'Thai', 'Greek', 'Hebrew',
    'Swedish', 'Norwegian', 'Danish', 'Finnish', 'Czech', 'Hungarian', 'Romanian', 'Bulgarian', 'Croatian', 'Serbian',
    'Ukrainian', 'Slovenian', 'Slovak', 'Lithuanian', 'Latvian', 'Estonian', 'Maltese', 'Irish', 'Welsh', 'Scots Gaelic',
    'Catalan', 'Basque', 'Galician', 'Afrikaans', 'Swahili', 'Amharic', 'Yoruba', 'Igbo', 'Hausa', 'Zulu',
    'Xhosa', 'Somali', 'Oromo', 'Tigrinya', 'Malagasy', 'Shona', 'Kinyarwanda', 'Kirundi', 'Luganda', 'Kikuyu',
    'Urdu', 'Punjabi', 'Gujarati', 'Marathi', 'Telugu', 'Tamil', 'Kannada', 'Malayalam', 'Odia', 'Assamese',
    'Nepali', 'Sinhala', 'Burmese', 'Khmer', 'Lao', 'Mongolian', 'Tibetan', 'Kazakh', 'Kyrgyz', 'Tajik',
    'Turkmen', 'Uzbek', 'Pashto', 'Dari', 'Kurdish', 'Persian/Farsi', 'Armenian', 'Georgian', 'Azerbaijani', 'Albanian',
    'Macedonian', 'Bosnian', 'Montenegrin', 'Icelandic', 'Faroese', 'Luxembourgish', 'Romansh', 'Sardinian', 'Corsican', 'Breton',
    'Malay', 'Indonesian', 'Tagalog', 'Cebuano', 'Ilocano', 'Hiligaynon', 'Waray', 'Bikol', 'Kapampangan', 'Pangasinan',
    'Javanese', 'Sundanese', 'Balinese', 'Minangkabau', 'Acehnese', 'Batak', 'Banjar', 'Buginese', 'Makassarese', 'Torajan'
  ].sort();

  // Comprehensive country list
  // AP Subjects list
  const apSubjects = [
    'Art History', 'Biology', 'Calculus AB', 'Calculus BC', 'Chemistry', 'Chinese Language & Culture',
    'Comparative Government & Politics', 'Computer Science A', 'Computer Science Principles',
    'English Language and Composition', 'English Literature and Composition', 'Environmental Science',
    'European History', 'French Language & Culture', 'German Language & Culture', 'Human Geography',
    'Italian Language & Culture', 'Japanese Language and Culture', 'Latin', 'Macroeconomics',
    'Microeconomics', 'Music Theory', 'Physics 1: Algebra-Based', 'Physics 2: Algebra-Based',
    'Physics C: Electricity & Magnetism', 'Physics C: Mechanics', 'Psychology', 'Research',
    'Seminar', 'Spanish Language & Culture', 'Spanish Literature & Culture', 'Statistics',
    'Studio Art: 2-D Design', 'Studio Art: 3-D Design', 'Studio Art: Drawing',
    'U.S. Government & Politics', 'U.S. History', 'World History'
  ];

  // IB Subjects list
  const ibSubjects = [
    'Afrikaans A: Literature', 'Albanian A: Literature', 'Amharic A: Literature', 'Arabic A Language and Literature',
    'Arabic A: Literature', 'Arabic Ab Initio', 'Arabic B', 'Armenian A: Literature', 'Art History', 'Astronomy',
    'Azerbaijani A: Literature', 'Belarusian A: Literature', 'Bemba A: Literature', 'Bengali A: Literature',
    'Biology', 'Bosnian A: Literature', 'Brazilian Social Studies', 'Bulgarian A: Literature', 'Burmese A: Literature',
    'Business Management', 'Catalan A: Literature', 'Chemistry', 'Chichewa A: Literature', 'Chinese A Language and Literature',
    'Chinese A: Literature', 'Chinese B - Cantonese', 'Chinese B - Mandarin', 'Classic Greek and Roman Studies',
    'Classic Greek', 'Computer Science', 'Croatian A: Literature', 'Czech A: Literature', 'Dance',
    'Danish A: Literature', 'Danish Ab Initio', 'Danish B', 'Design Technology', 'Digital Societies',
    'Dutch A Language and Literature', 'Dutch A: Literature', 'Dutch Ab Initio', 'Dutch B', 'Dzongkha A: Literature',
    'Economics', 'English A Language and Literature', 'English A: Literature', 'English Ab Initio', 'English B',
    'Environmental Systems and Societies', 'Estonian A: Literature', 'Filipino A: Literature', 'Film',
    'Finnish A: Literature', 'Finnish B', 'Food Science and Technology', 'French A Language and Literature',
    'French A: Literature', 'French Ab Initio', 'French B', 'Geography', 'Georgian A: Literature',
    'German A Language and Literature', 'German A: Literature', 'German Ab Initio', 'German B', 'Global Politics',
    'Guarani A: Literature', 'Hebrew A: Literature', 'Hebrew B', 'Hindi A: Literature', 'Hindi B', 'History',
    'History Africa and Middle East', 'History Americas', 'History Asia and Oceania', 'History Europe',
    'Hungarian A: Literature', 'Icelandic A: Literature', 'Indonesian A: Language and Literature', 'Indonesian A: Literature',
    'Indonesian Ab Initio', 'Indonesian B', 'Italian A Language and Literature', 'Italian A: Literature',
    'Italian Ab Initio', 'Italian B', 'Japanese A Language and Literature', 'Japanese A: Literature',
    'Japanese Ab Initio', 'Japanese B', 'Kazakh A: Literature', 'Khmer A: Literature', 'Kinyarwanda A: Literature',
    'Kiswahili A: Literature', 'Kiswahili Ab Initio', 'Kiswahili B', 'Korean A: Language and Literature',
    'Korean A: Literature', 'Korean B', 'Language and Culture', 'Lao A: Literature', 'Latin', 'Latvian A: Literature',
    'Literary Art', 'Literature and Performance', 'Lithuanian A: Literature', 'Luganda A: Literature',
    'Macedonian A: Literature', 'Malagasy A: Literature', 'Malay A: Literature', 'Malay B', 'Malayalam A: Literature',
    'Mandarin Ab Initio', 'Marine Science', 'Mathematics Analysis and Approaches', 'Mathematics Applications and Interpretations',
    'Modern Greek A Language and Literature', 'Modern Greek A: Literature', 'Mongolian A: Literature', 'Music',
    'Nature of Science', 'Ndebele A: Literature', 'Nepali A: Literature', 'Norwegian A Language and Literature',
    'Norwegian A: Literature', 'Norwegian B', 'Persian A: Literature', 'Philosophy', 'Physics', 'Polish A: Literature',
    'Portuguese A Language and Literature', 'Portuguese A: Literature', 'Portuguese B', 'Psychology',
    'Punjabi A: Literature', 'Reflective Project', 'Romanian A: Literature', 'Russian A Language and Literature',
    'Russian A: Literature', 'Russian Ab Initio', 'Russian B', 'Serbian A: Literature', 'Sesotho A: Literature',
    'Shona A: Literature', 'Sinhala A: Literature', 'Siswati A: Literature', 'Slovak A: Literature',
    'Slovene A: Literature', 'Social and Cultural Anthropology', 'Somali A: Literature', 'Spanish A Language and Literature',
    'Spanish A: Literature', 'Spanish Ab Initio', 'Spanish B', 'Sports Exercise and Health Science',
    'Swedish A Language and Literature', 'Swedish A: Literature', 'Swedish Ab Initio', 'Swedish B',
    'Tajik A: Literature', 'Tamil A: Literature', 'Tamil B', 'Telugu A: Literature', 'Thai A Language and Literature',
    'Thai A: Literature', 'Theatre', 'Tibetan A: Literature', 'Turkey in the 20th Century', 'Turkish A: Literature',
    'Ukrainian A: Literature', 'Urdu A: Literature', 'Uyghur A: Literature', 'Vietnamese A: Language and Literature',
    'Vietnamese A: Literature', 'Visual Arts', 'Welsh A: Literature', 'World Arts and Cultures', 'World Religions'
  ];

  // SAT Subject Tests list
  const satSubjectOptions = [
    'Biology - Ecological', 'Biology - Molecular', 'Chemistry', 'Chinese with Listening',
    'French Reading', 'French with Listening', 'German Reading', 'German with Listening',
    'Italian Reading', 'Japanese with Listening', 'Korean with Listening', 'Latin Reading',
    'Literature', 'Math Level 1', 'Math Level 2', 'Modern Hebrew Reading', 'Physics',
    'Spanish Reading', 'Spanish with Listening', 'US History', 'World History', 'Writing'
  ];

  // Generate SAT score options (200-800 in increments of 10)
  const satScoreOptions = Array.from({ length: 61 }, (_, i) => 200 + (i * 10));

  // Generate SAT Essay score options (6-24)
  const satEssayScoreOptions = Array.from({ length: 19 }, (_, i) => i + 6);

  // Generate ACT score options (1-36)
  const actScoreOptions = Array.from({ length: 36 }, (_, i) => i + 1);

  // Generate ACT Writing score options (2-12)
  const actWritingScoreOptions = Array.from({ length: 11 }, (_, i) => i + 2);

  // Get unique dates from all ACT fields for autofill suggestions
  const getUniqueDates = () => {
    const allDates = [
      actScores.composite.date,
      actScores.math.date,
      actScores.english.date,
      actScores.science.date,
      actScores.reading.date,
      writingScore.date
    ].filter(date => date && date.trim() !== '');
    
    return [...new Set(allDates)];
  };

  // Calculate composite score from section scores
  const calculateCompositeScore = () => {
    const sectionScores = [
      parseInt(actScores.math.score) || 0,
      parseInt(actScores.english.score) || 0,
      parseInt(actScores.science.score) || 0,
      parseInt(actScores.reading.score) || 0
    ].filter(score => score > 0);

    if (sectionScores.length === 4) {
      return Math.round(sectionScores.reduce((sum, score) => sum + score, 0) / 4);
    }
    return null;
  };

  // Get the most recent date from section scores for composite
  const getCompositeDate = () => {
    const sectionDates = [
      actScores.math.date,
      actScores.english.date,
      actScores.science.date,
      actScores.reading.date
    ].filter(date => date && date.trim() !== '');

    if (sectionDates.length > 0) {
      // Return the most recent date (assuming MM/YYYY format)
      return sectionDates.sort().pop() || '';
    }
    return '';
  };

  // Update ACT scores with automatic composite calculation
  const updateActScore = (section: string, field: 'score' | 'date', value: string) => {
    if (section === 'writing') {
      setWritingScore(prev => ({ ...prev, [field]: value }));
    } else if (section === 'composite') {
      // Allow manual editing of composite score and date
      setActScores(prev => ({
        ...prev,
        composite: { ...prev.composite, [field]: value }
      }));
    } else {
      setActScores(prev => {
        const newScores = {
          ...prev,
          [section]: { ...prev[section as keyof typeof prev], [field]: value }
        };
        
        return newScores;
      });
    }
  };

  // Auto-calculate composite score whenever section scores change
  React.useEffect(() => {
    const calculatedScore = calculateCompositeScore();
    const calculatedDate = getCompositeDate();
    
    if (calculatedScore !== null) {
      setActScores(prev => ({
        ...prev,
        composite: {
          score: calculatedScore.toString(),
          date: calculatedDate
        }
      }));
    }
  }, [actScores.math.score, actScores.english.score, actScores.science.score, actScores.reading.score]);

  // Calculate SAT total score from section scores
  const calculateSatTotalScore = () => {
    const readingWritingScore = parseInt(satScores.readingWriting.score) || 0;
    const mathScore = parseInt(satScores.math.score) || 0;

    if (readingWritingScore > 0 && mathScore > 0) {
      return readingWritingScore + mathScore;
    }
    return null;
  };

  // Get the most recent date from SAT section scores for total
  const getSatTotalDate = () => {
    const sectionDates = [
      satScores.readingWriting.date,
      satScores.math.date
    ].filter(date => date && date.trim() !== '');

    if (sectionDates.length > 0) {
      return sectionDates.sort().pop() || '';
    }
    return '';
  };

  // Update SAT scores
  const updateSatScore = (section: string, field: 'score' | 'date', value: string) => {
    if (section === 'total') {
      // Allow manual editing of total score and date
      setSatScores(prev => ({
        ...prev,
        total: { ...prev.total, [field]: value }
      }));
    } else {
      setSatScores(prev => ({
        ...prev,
        [section]: { ...prev[section as keyof typeof prev], [field]: value }
      }));
    }
  };

  // Auto-calculate SAT total score whenever section scores change
  React.useEffect(() => {
    const calculatedScore = calculateSatTotalScore();
    const calculatedDate = getSatTotalDate();
    
    if (calculatedScore !== null) {
      setSatScores(prev => ({
        ...prev,
        total: {
          score: calculatedScore.toString(),
          date: calculatedDate
        }
      }));
    }
  }, [satScores.readingWriting.score, satScores.math.score]);

  // Calculate TOEFL total score from section scores
  const calculateToeflTotalScore = () => {
    const readingScore = parseInt(toeflScores.reading.score) || 0;
    const writingScore = parseInt(toeflScores.writing.score) || 0;
    const speakingScore = parseInt(toeflScores.speaking.score) || 0;
    const listeningScore = parseInt(toeflScores.listening.score) || 0;

    if (readingScore > 0 && writingScore > 0 && speakingScore > 0 && listeningScore > 0) {
      return readingScore + writingScore + speakingScore + listeningScore;
    }
    return null;
  };

  // Get the most recent date from TOEFL section scores for total
  const getToeflTotalDate = () => {
    const sectionDates = [
      toeflScores.reading.date,
      toeflScores.writing.date,
      toeflScores.speaking.date,
      toeflScores.listening.date
    ].filter(date => date && date.trim() !== '');

    if (sectionDates.length > 0) {
      return sectionDates.sort().pop() || '';
    }
    return '';
  };

  // Update TOEFL scores
  const updateToeflScore = (section: string, field: 'score' | 'date', value: string) => {
    if (section === 'total') {
      // Allow manual editing of total score and date
      setToeflScores(prev => ({
        ...prev,
        total: { ...prev.total, [field]: value }
      }));
    } else {
      setToeflScores(prev => ({
        ...prev,
        [section]: { ...prev[section as keyof typeof prev], [field]: value }
      }));
    }
    setHasUnsavedChanges(true);
  };

  // Auto-calculate TOEFL total score whenever section scores change
  React.useEffect(() => {
    const calculatedScore = calculateToeflTotalScore();
    const calculatedDate = getToeflTotalDate();
    
    if (calculatedScore !== null) {
      setToeflScores(prev => ({
        ...prev,
        total: {
          score: calculatedScore.toString(),
          date: calculatedDate
        }
      }));
    }
  }, [toeflScores.reading.score, toeflScores.writing.score, toeflScores.speaking.score, toeflScores.listening.score]);

  // Calculate Duolingo total score (average of 4 sections)
  const calculateDuolingoTotalScore = () => {
    const literacyScore = parseInt(duolingoScores.literacy.score) || 0;
    const comprehensionScore = parseInt(duolingoScores.comprehension.score) || 0;
    const conversationScore = parseInt(duolingoScores.conversation.score) || 0;
    const productionScore = parseInt(duolingoScores.production.score) || 0;

    if (literacyScore > 0 && comprehensionScore > 0 && conversationScore > 0 && productionScore > 0) {
      return Math.round((literacyScore + comprehensionScore + conversationScore + productionScore) / 4);
    }
    return null;
  };

  // Get the most recent date from Duolingo section scores for total
  const getDuolingoTotalDate = () => {
    const sectionDates = [
      duolingoScores.literacy.date,
      duolingoScores.comprehension.date,
      duolingoScores.conversation.date,
      duolingoScores.production.date
    ].filter(date => date && date.trim() !== '');

    if (sectionDates.length > 0) {
      return sectionDates.sort().pop() || '';
    }
    return '';
  };

  // Update Duolingo scores
  const updateDuolingoScore = (section: string, field: 'score' | 'date', value: string) => {
    if (section === 'total') {
      // Allow manual editing of total score and date
      setDuolingoScores(prev => ({
        ...prev,
        total: { ...prev.total, [field]: value }
      }));
    } else {
      setDuolingoScores(prev => ({
        ...prev,
        [section]: { ...prev[section as keyof typeof prev], [field]: value }
      }));
    }
    setHasUnsavedChanges(true);
  };

  // Auto-calculate Duolingo total score whenever section scores change
  React.useEffect(() => {
    const calculatedScore = calculateDuolingoTotalScore();
    const calculatedDate = getDuolingoTotalDate();
    
    if (calculatedScore !== null) {
      setDuolingoScores(prev => ({
        ...prev,
        total: {
          score: calculatedScore.toString(),
          date: calculatedDate
        }
      }));
    }
  }, [duolingoScores.literacy.score, duolingoScores.comprehension.score, duolingoScores.conversation.score, duolingoScores.production.score]);

  // Calculate IELTS overall score (average of 4 sections)
  const calculateIeltsOverallScore = () => {
    const readingScore = parseFloat(ieltsScores.reading.score) || 0;
    const writingScore = parseFloat(ieltsScores.writing.score) || 0;
    const listeningScore = parseFloat(ieltsScores.listening.score) || 0;
    const speakingScore = parseFloat(ieltsScores.speaking.score) || 0;

    if (readingScore > 0 && writingScore > 0 && listeningScore > 0 && speakingScore > 0) {
      const average = (readingScore + writingScore + listeningScore + speakingScore) / 4;
      // Round to nearest 0.5
      return Math.round(average * 2) / 2;
    }
    return null;
  };

  // Get the most recent date from IELTS section scores for overall
  const getIeltsOverallDate = () => {
    const sectionDates = [
      ieltsScores.reading.date,
      ieltsScores.writing.date,
      ieltsScores.listening.date,
      ieltsScores.speaking.date
    ].filter(date => date && date.trim() !== '');

    if (sectionDates.length > 0) {
      return sectionDates.sort().pop() || '';
    }
    return '';
  };

  // Update IELTS scores
  const updateIeltsScore = (section: string, field: 'score' | 'date', value: string) => {
    if (section === 'overall') {
      // Allow manual editing of overall score and date
      setIeltsScores(prev => ({
        ...prev,
        overall: { ...prev.overall, [field]: value }
      }));
    } else {
      setIeltsScores(prev => ({
        ...prev,
        [section]: { ...prev[section as keyof typeof prev], [field]: value }
      }));
    }
    setHasUnsavedChanges(true);
  };

  // Auto-calculate IELTS overall score whenever section scores change
  React.useEffect(() => {
    const calculatedScore = calculateIeltsOverallScore();
    const calculatedDate = getIeltsOverallDate();
    
    if (calculatedScore !== null) {
      setIeltsScores(prev => ({
        ...prev,
        overall: {
          score: calculatedScore.toString(),
          date: calculatedDate
        }
      }));
    }
  }, [ieltsScores.reading.score, ieltsScores.writing.score, ieltsScores.listening.score, ieltsScores.speaking.score]);

  // Update PTE scores (no overall calculation needed)
  const updatePteScore = (section: string, field: 'score' | 'date', value: string) => {
    setPteScores(prev => ({
      ...prev,
      [section]: { ...prev[section as keyof typeof prev], [field]: value }
    }));
    setHasUnsavedChanges(true);
  };

  // Update Cambridge qualification
  const updateCambridgeQualification = (field: 'proficiency_level' | 'exam_date', value: string) => {
    setCambridgeQualification(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
  };

  // Cambridge Tests subjects (cleaned up list)
  const cambridgeSubjects = [
    'Accounting', 'Afrikaans - Second Language', 'Agriculture', 'Arabic - First Language', 
    'Arabic - Foreign Language', 'Art & Design', 'Bahasa Indonesia', 'Biology', 'Business', 
    'Business Studies', 'Chemistry', 'Chinese - First Language', 'Chinese - Second Language', 
    'Chinese (Mandarin) - Foreign Language', 'Commerce', 'Computer Science', 'Design & Technology', 
    'Drama', 'Economics', 'English - First Language', 'English - Literature', 'English (as an Additional Language)', 
    'English as a Second Language', 'Enterprise', 'Environmental Management', 'Food & Nutrition', 
    'French - First Language', 'French - Foreign Language', 'Geography', 'German - First Language', 
    'German - Foreign Language', 'Global Perspectives', 'Hindi as a Second Language', 'History', 
    'History - American (US)', 'Information and Communication Technology', 'IsiZulu as a Second Language', 
    'Islamiyat', 'Italian - Foreign Language', 'Japanese - Foreign Language'
  ];

  const allCountries = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
    'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
    'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia',
    'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo (Democratic Republic)', 'Congo (Republic)',
    'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador',
    'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France',
    'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau',
    'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
    'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Korea (North)', 'Korea (South)',
    'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania',
    'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius',
    'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia',
    'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Macedonia', 'Norway', 'Oman',
    'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
    'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe',
    'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
    'South Africa', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan',
    'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan',
    'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City',
    'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
  ].sort();

  const toggleLanguage = (language: string) => {
    if (selectedLanguages.includes(language)) {
      // Remove language and its proficiencies
      setSelectedLanguages(selectedLanguages.filter(lang => lang !== language));
      const newProficiencies = { ...languageProficiencies };
      delete newProficiencies[language];
      setLanguageProficiencies(newProficiencies);
    } else {
      // Add language with default proficiencies
      setSelectedLanguages([...selectedLanguages, language]);
      setLanguageProficiencies({
        ...languageProficiencies,
        [language]: ['Speak'] // Default to just 'Speak'
      });
    }
  };

  const toggleLanguageProficiency = (language: string, proficiency: string) => {
    const currentProficiencies = languageProficiencies[language] || [];
    const newProficiencies = currentProficiencies.includes(proficiency)
      ? currentProficiencies.filter(p => p !== proficiency)
      : [...currentProficiencies, proficiency];
    
    setLanguageProficiencies({
      ...languageProficiencies,
      [language]: newProficiencies
    });
  };

  // International phone number validation
  const validatePhoneNumber = (phone: string) => {
    if (!phone.trim()) {
      return 'Phone number is required';
    }
    
    // More flexible international phone regex
    // Supports formats like: +1234567890, +1 234 567 8900, (123) 456-7890, etc.
    const internationalPhoneRegex = /^[\+]?[1-9][\d]{0,15}$|^[\+]?[(]?[\d\s\-\(\)]{7,20}$/;
    
    // Remove all non-digit characters except + for validation
    const cleanPhone = phone.replace(/[^\d+]/g, '');
    
    if (cleanPhone.length < 7) {
      return 'Phone number is too short';
    }
    
    if (cleanPhone.length > 16) {
      return 'Phone number is too long';
    }
    
    if (!internationalPhoneRegex.test(phone)) {
      return 'Please enter a valid phone number';
    }
    
    return '';
  };

  const handlePhoneChange = (value: string) => {
    setPhoneNumber(value);
    const error = validatePhoneNumber(value);
    setPhoneError(error);
    setHasUnsavedChanges(true);
  };



  // Close language dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.settings-multiselect-container')) {
        setShowLanguageDropdown(false);
      }
    };

    if (showLanguageDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showLanguageDropdown]);

  return (
    <div className="settings-page dark:bg-gray-900">
      <div className="settings-container dark:bg-gray-900">
        {/* Header */}
        <header className="settings-header">
          <div className="settings-header-content dark:bg-gray-800 dark:border-gray-700">
            <div className="settings-header-left">
              <h1 className="settings-title dark:text-gray-100">Settings</h1>
              <div className="settings-search dark:bg-gray-700">
                <IoSearch className="settings-search-icon dark:text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Search settings..." 
                  className="settings-search-input dark:text-gray-200 dark:placeholder-gray-400"
                />
              </div>
            </div>
            <button 
              className="settings-save-button dark:bg-blue-600 dark:hover:bg-blue-700"
              onClick={handleSaveProfile}
              disabled={isSaving || !hasUnsavedChanges}
            >
              <IoSave className="settings-save-icon" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </header>

        {/* Navigation */}
        <div className="settings-navigation dark:bg-gray-800 dark:border-gray-700">
          <button 
            className={`settings-nav-button ${activeTab === 'profile' ? 'active' : ''} dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 ${activeTab === 'profile' ? 'dark:!bg-blue-600 dark:!text-white' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <IoPerson className="settings-nav-icon" />
            Profile
          </button>
          <button 
            className={`settings-nav-button ${activeTab === 'general' ? 'active' : ''} dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 ${activeTab === 'general' ? 'dark:!bg-blue-600 dark:!text-white' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            <IoOptions className="settings-nav-icon" />
            General
          </button>
          <button className="settings-nav-button dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
            <IoPersonCircle className="settings-nav-icon" />
            Account
          </button>
          <button 
            className={`settings-nav-button ${activeTab === 'billing' ? 'active' : ''} dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 ${activeTab === 'billing' ? 'dark:!bg-blue-600 dark:!text-white' : ''}`}
            onClick={() => setActiveTab('billing')}
          >
            <IoCard className="settings-nav-icon" />
            Billing
          </button>
          <button 
            className={`settings-nav-button ${activeTab === 'notifications' ? 'active' : ''} dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 ${activeTab === 'notifications' ? 'dark:!bg-blue-600 dark:!text-white' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <IoNotificationsOutline className="settings-nav-icon" />
            Notifications
          </button>
          <button className="settings-nav-button dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
            <IoShieldHalf className="settings-nav-icon" />
            Privacy
          </button>
        </div>

        {/* Content */}
        <div className="settings-content">
          {activeTab === 'general' ? (
            <>
              {/* General Settings Header */}
              <div className="settings-section-header">
                <h2 className="settings-section-title dark:text-gray-100">General Preferences</h2>
                <p className="settings-section-description dark:text-gray-400">Customize your application portal experience and preferences</p>
              </div>

              {/* Appearance and Accessibility Side by Side */}
              <div className="settings-general-grid">
                {/* Appearance */}
                <div className="settings-card dark:bg-gray-800 dark:border-gray-700">
                  <div className="settings-card-header">
                  <div>
                    <h3 className="settings-card-title dark:text-gray-100">Appearance</h3>
                    <p className="settings-card-description dark:text-gray-400">Customize theme, language, time zone, and date format</p>
                  </div>
                </div>
                  <div className="settings-card-content">
                    <div className="settings-form-section">
                      <label className="settings-label dark:text-gray-200">Theme</label>
                      <div className="settings-theme-grid">
                        <div 
                          className={`settings-theme-option ${theme === 'Light' ? 'active dark:border-blue-500' : 'dark:border-gray-600 dark:hover:border-gray-500'} dark:bg-gray-700`}
                          onClick={() => handleThemeChange('Light')}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="settings-theme-header">
                            <span className="settings-theme-name dark:text-gray-200">Light Mode</span>
                            <svg className="w-5 h-5" style={{ color: theme === 'Light' ? '#6B7AE8' : '#9ca3af' }} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d={theme === 'Light' ? "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" : "M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z"} clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="settings-theme-preview light">
                            <div className="settings-theme-preview-bar"></div>
                            <div className="settings-theme-preview-bar short"></div>
                          </div>
                        </div>
                        <div 
                          className={`settings-theme-option ${theme === 'Dark' ? 'active dark:border-blue-500' : 'dark:border-gray-600 dark:hover:border-gray-500'} dark:bg-gray-700`}
                          onClick={() => handleThemeChange('Dark')}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="settings-theme-header">
                            <span className="settings-theme-name dark:text-gray-200">Dark Mode</span>
                            <svg className="w-5 h-5" style={{ color: theme === 'Dark' ? '#6B7AE8' : '#9ca3af' }} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d={theme === 'Dark' ? "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" : "M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z"} clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="settings-theme-preview dark">
                            <div className="settings-theme-preview-bar"></div>
                            <div className="settings-theme-preview-bar short"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="settings-form-section">
                      <label className="settings-label dark:text-gray-200">Language</label>
                      <select className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200">
                        <option>English (US)</option>
                        <option>English (UK)</option>
                        <option>Spanish</option>
                        <option>French</option>
                        <option>German</option>
                        <option>Chinese (Simplified)</option>
                        <option>Japanese</option>
                        <option>Korean</option>
                      </select>
                    </div>
                    <div className="settings-form-section">
                      <label className="settings-label dark:text-gray-200">Time Zone</label>
                      <select 
                        className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                        value={selectedTimezone}
                        onChange={(e) => {
                          setSelectedTimezone(e.target.value);
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <option value="Eastern Time (ET)">Eastern Time (ET)</option>
                        <option value="Central Time (CT)">Central Time (CT)</option>
                        <option value="Mountain Time (MT)">Mountain Time (MT)</option>
                        <option value="Pacific Time (PT)">Pacific Time (PT)</option>
                        <option value="Alaska Time (AKT)">Alaska Time (AKT)</option>
                        <option value="Hawaii-Aleutian Time (HAT)">Hawaii-Aleutian Time (HAT)</option>
                      </select>
                    </div>
                    <div className="settings-form-section">
                      <label className="settings-label dark:text-gray-200">Date Format</label>
                      <div className="settings-date-format-grid">
                        <div 
                          className={`settings-date-format-option ${selectedDateFormat === 'MM/DD/YYYY' ? 'active' : ''} dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 cursor-pointer`}
                          onClick={() => {
                            setSelectedDateFormat('MM/DD/YYYY');
                            setHasUnsavedChanges(true);
                          }}
                        >
                          <p className="settings-date-format-label dark:text-gray-200">MM/DD/YYYY</p>
                          <p className="settings-date-format-example dark:text-gray-400">01/10/2025</p>
                        </div>
                        <div 
                          className={`settings-date-format-option ${selectedDateFormat === 'DD/MM/YYYY' ? 'active' : ''} dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 cursor-pointer`}
                          onClick={() => {
                            setSelectedDateFormat('DD/MM/YYYY');
                            setHasUnsavedChanges(true);
                          }}
                        >
                          <p className="settings-date-format-label dark:text-gray-200">DD/MM/YYYY</p>
                          <p className="settings-date-format-example dark:text-gray-400">10/01/2025</p>
                        </div>
                        <div 
                          className={`settings-date-format-option ${selectedDateFormat === 'YYYY-MM-DD' ? 'active' : ''} dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 cursor-pointer`}
                          onClick={() => {
                            setSelectedDateFormat('YYYY-MM-DD');
                            setHasUnsavedChanges(true);
                          }}
                        >
                          <p className="settings-date-format-label dark:text-gray-200">YYYY-MM-DD</p>
                          <p className="settings-date-format-example dark:text-gray-400">2025-01-10</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Accessibility */}
                <div className="settings-card dark:bg-gray-800 dark:border-gray-700">
                  <div className="settings-card-header">
                    <div>
                      <h3 className="settings-card-title dark:text-gray-100">Accessibility</h3>
                      <p className="settings-card-description dark:text-gray-400">Adjust settings for better accessibility and usability</p>
                    </div>
                  </div>
                  <div className="settings-card-content">
                      <div className="settings-form-section">
                        <label className="settings-label dark:text-gray-200">Text Size</label>
                        <div className="settings-range-container">
                          <span className="settings-range-label dark:text-gray-400">Small</span>
                          <input 
                            type="range" 
                            min="1" 
                            max="5" 
                            value={textSize}
                            onChange={(e) => {
                              const newSize = parseInt(e.target.value);
                              setTextSize(newSize);
                              setHasUnsavedChanges(true);
                              // Apply text size immediately to document root
                              document.documentElement.style.fontSize = `${0.8 + (newSize - 1) * 0.1}rem`;
                            }}
                            className="settings-range-input" 
                          />
                          <span className="settings-range-label dark:text-gray-400">Large</span>
                        </div>
                        <p className="settings-range-hint dark:text-gray-400">
                          Current size: {textSize === 1 ? 'Extra Small' : textSize === 2 ? 'Small' : textSize === 3 ? 'Medium' : textSize === 4 ? 'Large' : 'Extra Large'}
                        </p>
                      </div>
                      <div className="settings-toggle-item">
                        <div className="settings-toggle-content">
                          <p className="settings-toggle-title dark:text-gray-200">High contrast mode</p>
                          <p className="settings-toggle-description dark:text-gray-400">Increase color contrast for better visibility</p>
                        </div>
                        <label className="settings-toggle-switch">
                          <input 
                            type="checkbox" 
                            className="settings-toggle-input"
                            checked={highContrastMode}
                            onChange={(e) => {
                              setHighContrastMode(e.target.checked);
                              setHasUnsavedChanges(true);
                            }}
                          />
                          <span className="settings-toggle-slider"></span>
                        </label>
                      </div>
                      <div className="settings-toggle-item">
                        <div className="settings-toggle-content">
                          <p className="settings-toggle-title dark:text-gray-200">Screen reader support</p>
                          <p className="settings-toggle-description dark:text-gray-400">Optimize interface for screen readers</p>
                        </div>
                        <label className="settings-toggle-switch">
                          <input 
                            type="checkbox" 
                            className="settings-toggle-input"
                            checked={screenReaderOptimized}
                            onChange={(e) => {
                              setScreenReaderOptimized(e.target.checked);
                              setHasUnsavedChanges(true);
                            }}
                          />
                          <span className="settings-toggle-slider"></span>
                        </label>
                      </div>
                      <div className="settings-toggle-item">
                        <div className="settings-toggle-content">
                          <p className="settings-toggle-title dark:text-gray-200">Reduce motion</p>
                          <p className="settings-toggle-description dark:text-gray-400">Minimize animations and transitions</p>
                        </div>
                        <label className="settings-toggle-switch">
                          <input 
                            type="checkbox" 
                            className="settings-toggle-input"
                            checked={reducedMotion}
                            onChange={(e) => {
                              setReducedMotion(e.target.checked);
                              setHasUnsavedChanges(true);
                            }}
                          />
                          <span className="settings-toggle-slider"></span>
                        </label>
                      </div>
                      
                      {/* Platform Tour Button */}
                      <div className="settings-form-section mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => {
                            // Start tour immediately without navigation
                            startTour();
                          }}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 dark:bg-purple-700 dark:hover:bg-purple-600 text-white font-semibold rounded-lg transition-colors shadow-sm"
                        >
                          <IoPlayCircle className="text-xl" />
                          View Platform Tour
                        </button>
                        <p className="settings-range-hint dark:text-gray-400 mt-2 text-center">
                          Take a guided tour of Apply.AI's features
                        </p>
                      </div>
                    </div>
                </div>
              </div>

              {/* Dashboard Preferences */}
              <div className="settings-card dark:bg-gray-800 dark:border-gray-700">
                <div 
                  className="settings-card-header" 
                  onClick={() => toggleSection('dashboard-preferences')}
                >
                  <div>
                    <h3 className="settings-card-title dark:text-gray-100">Dashboard Preferences</h3>
                    <p className="settings-card-description dark:text-gray-400">Configure what appears on your dashboard</p>
                  </div>
                  <IoChevronDown 
                    className={`settings-chevron dark:text-gray-400 ${expandedSections['dashboard-preferences'] ? 'expanded' : ''}`}
                  />
                </div>
                {expandedSections['dashboard-preferences'] && (
                  <div className="settings-card-content">
                    <div className="settings-toggle-item">
                      <div className="settings-toggle-content">
                        <p className="settings-toggle-title dark:text-gray-200">Show upcoming deadlines</p>
                        <p className="settings-toggle-description dark:text-gray-400">Display deadline reminders on your dashboard</p>
                      </div>
                      <label className="settings-toggle-switch">
                        <input type="checkbox" defaultChecked className="settings-toggle-input" />
                        <span className="settings-toggle-slider"></span>
                      </label>
                    </div>
                    <div className="settings-toggle-item">
                      <div className="settings-toggle-content">
                        <p className="settings-toggle-title dark:text-gray-200">Enable AI suggestions</p>
                        <p className="settings-toggle-description dark:text-gray-400">Get personalized recommendations and insights</p>
                      </div>
                      <label className="settings-toggle-switch">
                        <input type="checkbox" defaultChecked className="settings-toggle-input" />
                        <span className="settings-toggle-slider"></span>
                      </label>
                    </div>
                    <div className="settings-toggle-item">
                      <div className="settings-toggle-content">
                        <p className="settings-toggle-title dark:text-gray-200">Show application analytics</p>
                        <p className="settings-toggle-description dark:text-gray-400">Display charts and progress statistics</p>
                      </div>
                      <label className="settings-toggle-switch">
                        <input type="checkbox" defaultChecked className="settings-toggle-input" />
                        <span className="settings-toggle-slider"></span>
                      </label>
                    </div>
                    <div className="settings-toggle-item">
                      <div className="settings-toggle-content">
                        <p className="settings-toggle-title dark:text-gray-200">Auto-save drafts</p>
                        <p className="settings-toggle-description dark:text-gray-400">Automatically save essay drafts every 30 seconds</p>
                      </div>
                      <label className="settings-toggle-switch">
                        <input type="checkbox" defaultChecked className="settings-toggle-input" />
                        <span className="settings-toggle-slider"></span>
                      </label>
                    </div>
                    <div className="settings-toggle-item">
                      <div className="settings-toggle-content">
                        <p className="settings-toggle-title dark:text-gray-200">Compact view</p>
                        <p className="settings-toggle-description dark:text-gray-400">Use a more condensed layout for dashboard</p>
                      </div>
                      <label className="settings-toggle-switch">
                        <input type="checkbox" className="settings-toggle-input" />
                        <span className="settings-toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : activeTab === 'billing' ? (
            <>
              {/* Billing Hero Section */}
              <div className="settings-billing-hero dark:bg-gradient-to-r dark:from-blue-900/30 dark:to-purple-900/30">
                <div className="settings-billing-hero-content">
                  <div>
                    <h2 className="settings-billing-hero-title dark:text-gray-100">Manage Your Subscription</h2>
                    <p className="settings-billing-hero-description dark:text-gray-300">View your current plan, billing history, and upgrade options</p>
                  </div>
                  <div className="settings-billing-hero-buttons">
                    <button className="settings-button-outline dark:bg-gray-800 dark:border-blue-500 dark:text-blue-400 dark:hover:bg-gray-700">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                      Download Invoices
                    </button>
                    <button className="settings-button-primary dark:bg-blue-600 dark:hover:bg-blue-700">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      Upgrade Plan
                    </button>
                  </div>
                </div>
              </div>

              {/* Current Plan Section */}
              <div className="settings-billing-section">
                <h2 className="settings-billing-section-title dark:text-gray-100">Current Plan</h2>
                <div className="settings-current-plan-card dark:bg-gray-800 dark:border-gray-700">
                  <div className="settings-current-plan-header">
                    <div>
                      <div className="settings-current-plan-title-row">
                        <svg className="w-6 h-6" style={{ color: '#fbbf24' }} fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <h3 className="settings-current-plan-name dark:text-gray-100">Premium Plan</h3>
                      </div>
                      <p className="settings-current-plan-subtitle dark:text-gray-300">Access to all features and unlimited AI reviews</p>
                    </div>
                    <span className="settings-badge-active">Active</span>
                  </div>

                  <div className="settings-current-plan-stats">
                    <div className="settings-plan-stat dark:border-gray-700">
                      <p className="settings-plan-stat-label dark:text-gray-300">Monthly Price</p>
                      <p className="settings-plan-stat-value dark:text-gray-100">$29.99</p>
                      <p className="settings-plan-stat-hint dark:text-gray-400">Billed monthly</p>
                    </div>
                    <div className="settings-plan-stat dark:border-gray-700">
                      <p className="settings-plan-stat-label dark:text-gray-300">Next Billing Date</p>
                      <p className="settings-plan-stat-value dark:text-gray-100">Feb 10, 2025</p>
                      <p className="settings-plan-stat-hint dark:text-gray-400">Auto-renewal enabled</p>
                    </div>
                    <div className="settings-plan-stat dark:border-gray-700">
                      <p className="settings-plan-stat-label dark:text-gray-300">Member Since</p>
                      <p className="settings-plan-stat-value dark:text-gray-100">Sep 2024</p>
                      <p className="settings-plan-stat-hint dark:text-gray-400">5 months active</p>
                    </div>
                  </div>

                  <div className="settings-current-plan-features">
                    <h4 className="settings-plan-features-title dark:text-gray-200">Plan Includes:</h4>
                    <div className="settings-plan-features-grid">
                      <div className="settings-plan-feature-item dark:text-gray-200">
                        <svg className="w-5 h-5" style={{ color: '#10b981' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Unlimited college applications</span>
                      </div>
                      <div className="settings-plan-feature-item dark:text-gray-200">
                        <svg className="w-5 h-5" style={{ color: '#10b981' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>AI essay review & feedback</span>
                      </div>
                      <div className="settings-plan-feature-item dark:text-gray-200">
                        <svg className="w-5 h-5" style={{ color: '#10b981' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Priority email support</span>
                      </div>
                      <div className="settings-plan-feature-item dark:text-gray-200">
                        <svg className="w-5 h-5" style={{ color: '#10b981' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Advanced analytics dashboard</span>
                      </div>
                      <div className="settings-plan-feature-item dark:text-gray-200">
                        <svg className="w-5 h-5" style={{ color: '#10b981' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Document storage (50GB)</span>
                      </div>
                      <div className="settings-plan-feature-item dark:text-gray-200">
                        <svg className="w-5 h-5" style={{ color: '#10b981' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Deadline reminders & alerts</span>
                      </div>
                      <div className="settings-plan-feature-item dark:text-gray-200">
                        <svg className="w-5 h-5" style={{ color: '#10b981' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>College comparison tools</span>
                      </div>
                      <div className="settings-plan-feature-item dark:text-gray-200">
                        <svg className="w-5 h-5" style={{ color: '#10b981' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Financial aid calculator</span>
                      </div>
                    </div>
                  </div>

                  <div className="settings-current-plan-actions">
                    <button className="settings-button-white dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">Change Plan</button>
                    <button className="settings-button-outline-white dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">Cancel Subscription</button>
                  </div>
                </div>
              </div>

              {/* Available Plans Section */}
              <div className="settings-billing-section">
                <div className="settings-plans-header">
                  <div>
                    <h2 className="settings-billing-section-title dark:text-gray-100">Available Plans</h2>
                    <p className="settings-billing-section-subtitle dark:text-gray-400">Choose the plan that best fits your needs</p>
                  </div>
                  <div className="settings-billing-toggle">
                    <button className="settings-billing-toggle-active">Monthly</button>
                    <button className="settings-billing-toggle-inactive">Annual <span className="settings-billing-toggle-save">(Save 20%)</span></button>
                  </div>
                </div>

                <div className="settings-plans-grid">
                  {/* Basic Plan */}
                  <div className="settings-plan-card dark:bg-gray-800 dark:border-gray-700">
                    <div className="settings-plan-card-header">
                      <h3 className="settings-plan-card-title dark:text-gray-100">Basic</h3>
                      <span className="settings-badge-gray dark:bg-gray-700 dark:text-gray-300">Free</span>
                    </div>
                    <div className="settings-plan-card-price">
                      <p className="settings-plan-card-amount dark:text-gray-100">$0</p>
                      <p className="settings-plan-card-period dark:text-gray-400">per month</p>
                    </div>
                    <ul className="settings-plan-card-features">
                      <li className="settings-plan-card-feature dark:text-gray-200">
                        <svg className="w-5 h-5" style={{ color: '#10b981' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Up to 3 applications</span>
                      </li>
                      <li className="settings-plan-card-feature dark:text-gray-200">
                        <svg className="w-5 h-5" style={{ color: '#10b981' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Basic profile management</span>
                      </li>
                      <li className="settings-plan-card-feature dark:text-gray-200">
                        <svg className="w-5 h-5" style={{ color: '#10b981' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Deadline tracking</span>
                      </li>
                      <li className="settings-plan-card-feature dark:text-gray-200">
                        <svg className="w-5 h-5" style={{ color: '#10b981' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Email support</span>
                      </li>
                      <li className="settings-plan-card-feature dark:text-gray-200">
                        <svg className="w-5 h-5" style={{ color: '#10b981' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Document storage (5GB)</span>
                      </li>
                      <li className="settings-plan-card-feature disabled dark:text-gray-500">
                        <svg className="w-5 h-5" style={{ color: '#d1d5db' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        <span>AI essay review</span>
                      </li>
                      <li className="settings-plan-card-feature disabled dark:text-gray-500">
                        <svg className="w-5 h-5" style={{ color: '#d1d5db' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        <span>Advanced analytics</span>
                      </li>
                    </ul>
                    <button className="settings-plan-card-button-gray dark:bg-gray-700 dark:text-gray-300">Current Plan</button>
                  </div>

                  {/* Premium Plan */}
                  <div className="settings-plan-card settings-plan-card-featured dark:bg-gray-800 dark:border-blue-600">
                    <div className="settings-plan-card-badge dark:bg-blue-600">MOST POPULAR</div>
                    <div className="settings-plan-card-header">
                      <h3 className="settings-plan-card-title dark:text-gray-100">Premium</h3>
                      <span className="settings-badge-primary">Active</span>
                    </div>
                    <div className="settings-plan-card-price">
                      <p className="settings-plan-card-amount dark:text-blue-400" style={{ color: '#6B7AE8' }}>$29.99</p>
                      <p className="settings-plan-card-period dark:text-gray-400">per month</p>
                    </div>
                    <ul className="settings-plan-card-features">
                      <li className="settings-plan-card-feature">
                        <svg className="w-5 h-5" style={{ color: '#6B7AE8' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Unlimited applications</span>
                      </li>
                      <li className="settings-plan-card-feature">
                        <svg className="w-5 h-5" style={{ color: '#6B7AE8' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>AI essay review & feedback</span>
                      </li>
                      <li className="settings-plan-card-feature">
                        <svg className="w-5 h-5" style={{ color: '#6B7AE8' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Priority email support</span>
                      </li>
                      <li className="settings-plan-card-feature">
                        <svg className="w-5 h-5" style={{ color: '#6B7AE8' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Advanced analytics</span>
                      </li>
                      <li className="settings-plan-card-feature">
                        <svg className="w-5 h-5" style={{ color: '#6B7AE8' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Document storage (50GB)</span>
                      </li>
                      <li className="settings-plan-card-feature">
                        <svg className="w-5 h-5" style={{ color: '#6B7AE8' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>College comparison tools</span>
                      </li>
                      <li className="settings-plan-card-feature">
                        <svg className="w-5 h-5" style={{ color: '#6B7AE8' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Financial aid calculator</span>
                      </li>
                    </ul>
                    <button className="settings-plan-card-button-primary">Current Plan</button>
                  </div>

                  {/* Enterprise Plan */}
                  <div className="settings-plan-card">
                    <div className="settings-plan-card-header">
                      <h3 className="settings-plan-card-title">Enterprise</h3>
                      <span className="settings-badge-purple">Premium</span>
                    </div>
                    <div className="settings-plan-card-price">
                      <p className="settings-plan-card-amount">$49.99</p>
                      <p className="settings-plan-card-period">per month</p>
                    </div>
                    <ul className="settings-plan-card-features">
                      <li className="settings-plan-card-feature">
                        <svg className="w-5 h-5" style={{ color: '#9333ea' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Everything in Premium</span>
                      </li>
                      <li className="settings-plan-card-feature">
                        <svg className="w-5 h-5" style={{ color: '#9333ea' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>1-on-1 counselor sessions</span>
                      </li>
                      <li className="settings-plan-card-feature">
                        <svg className="w-5 h-5" style={{ color: '#9333ea' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>24/7 phone support</span>
                      </li>
                      <li className="settings-plan-card-feature">
                        <svg className="w-5 h-5" style={{ color: '#9333ea' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Custom essay templates</span>
                      </li>
                      <li className="settings-plan-card-feature">
                        <svg className="w-5 h-5" style={{ color: '#9333ea' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Unlimited storage</span>
                      </li>
                      <li className="settings-plan-card-feature">
                        <svg className="w-5 h-5" style={{ color: '#9333ea' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Interview preparation</span>
                      </li>
                      <li className="settings-plan-card-feature">
                        <svg className="w-5 h-5" style={{ color: '#9333ea' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Scholarship matching</span>
                      </li>
                    </ul>
                    <button className="settings-plan-card-button-purple">Upgrade to Enterprise</button>
                  </div>
                </div>
              </div>

              {/* Payment Method Section */}
              <div className="settings-billing-section">
                <h2 className="settings-billing-section-title">Payment Method</h2>
                <div className="settings-payment-method-card">
                  <div className="settings-payment-method-header">
                    <div className="settings-payment-method-info">
                      <div className="settings-payment-method-icon">
                        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                          <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="settings-payment-method-title">Visa ending in 4242</h3>
                        <p className="settings-payment-method-subtitle">Expires 12/2026</p>
                      </div>
                    </div>
                    <div className="settings-payment-method-actions">
                      <span className="settings-badge-success">Primary</span>
                      <button className="settings-edit-link">Edit</button>
                    </div>
                  </div>

                  <div className="settings-payment-method-billing">
                    <h4 className="settings-payment-method-billing-title">Billing Address</h4>
                    <div className="settings-payment-method-billing-grid">
                      <div>
                        <p className="settings-payment-method-billing-name">Sarah Johnson</p>
                        <p className="settings-payment-method-billing-text">1234 Oak Street</p>
                        <p className="settings-payment-method-billing-text">Springfield, IL 62701</p>
                        <p className="settings-payment-method-billing-text">United States</p>
                      </div>
                      <div>
                        <p className="settings-payment-method-billing-text"><span className="settings-payment-method-billing-label">Email:</span> sarah.johnson@email.com</p>
                        <p className="settings-payment-method-billing-text"><span className="settings-payment-method-billing-label">Phone:</span> (555) 123-4567</p>
                      </div>
                    </div>
                  </div>

                  <div className="settings-payment-method-buttons">
                    <button className="settings-button-primary">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                      Add Payment Method
                    </button>
                    <button className="settings-button-secondary">Update Billing Info</button>
                  </div>
                </div>
              </div>

              {/* Invoice History Section */}
              <div className="settings-billing-section">
                <div className="settings-invoice-header">
                  <h2 className="settings-billing-section-title">Invoice History</h2>
                  <div className="settings-invoice-actions">
                    <select className="settings-select">
                      <option>All Time</option>
                      <option>Last 3 Months</option>
                      <option>Last 6 Months</option>
                      <option>This Year</option>
                    </select>
                    <button className="settings-button-secondary">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                      Export All
                    </button>
                  </div>
                </div>

                <div className="settings-invoice-table-container">
                  <table className="settings-invoice-table">
                    <thead>
                      <tr>
                        <th>Invoice ID</th>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="settings-invoice-id">#INV-2025-001</td>
                        <td className="settings-invoice-date">Jan 10, 2025</td>
                        <td className="settings-invoice-description">Premium Plan - Monthly</td>
                        <td className="settings-invoice-amount">$29.99</td>
                        <td><span className="settings-badge-success">Paid</span></td>
                        <td>
                          <button className="settings-invoice-download">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            Download
                          </button>
                        </td>
                      </tr>
                      <tr>
                        <td className="settings-invoice-id">#INV-2024-012</td>
                        <td className="settings-invoice-date">Dec 10, 2024</td>
                        <td className="settings-invoice-description">Premium Plan - Monthly</td>
                        <td className="settings-invoice-amount">$29.99</td>
                        <td><span className="settings-badge-success">Paid</span></td>
                        <td>
                          <button className="settings-invoice-download">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            Download
                          </button>
                        </td>
                      </tr>
                      <tr>
                        <td className="settings-invoice-id">#INV-2024-011</td>
                        <td className="settings-invoice-date">Nov 10, 2024</td>
                        <td className="settings-invoice-description">Premium Plan - Monthly</td>
                        <td className="settings-invoice-amount">$29.99</td>
                        <td><span className="settings-badge-success">Paid</span></td>
                        <td>
                          <button className="settings-invoice-download">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            Download
                          </button>
                        </td>
                      </tr>
                      <tr>
                        <td className="settings-invoice-id">#INV-2024-010</td>
                        <td className="settings-invoice-date">Oct 10, 2024</td>
                        <td className="settings-invoice-description">Premium Plan - Monthly</td>
                        <td className="settings-invoice-amount">$29.99</td>
                        <td><span className="settings-badge-success">Paid</span></td>
                        <td>
                          <button className="settings-invoice-download">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            Download
                          </button>
                        </td>
                      </tr>
                      <tr>
                        <td className="settings-invoice-id">#INV-2024-009</td>
                        <td className="settings-invoice-date">Sep 10, 2024</td>
                        <td className="settings-invoice-description">Premium Plan - First Payment</td>
                        <td className="settings-invoice-amount">$29.99</td>
                        <td><span className="settings-badge-success">Paid</span></td>
                        <td>
                          <button className="settings-invoice-download">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            Download
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="settings-invoice-footer">
                  <p className="settings-invoice-footer-text">Showing 5 of 5 invoices</p>
                  <div className="settings-invoice-pagination">
                    <button className="settings-invoice-pagination-button" disabled>Previous</button>
                    <button className="settings-invoice-pagination-button settings-invoice-pagination-active">1</button>
                    <button className="settings-invoice-pagination-button" disabled>Next</button>
                  </div>
                </div>
              </div>

              {/* Billing FAQ Section */}
              <div className="settings-billing-section">
                <h2 className="settings-billing-section-title">Billing FAQ</h2>
                <div className="settings-faq-container">
                  <div className="settings-faq-item">
                    <button className="settings-faq-question">
                      <h3 className="settings-faq-question-text">How do I cancel my subscription?</h3>
                      <IoChevronDown className="settings-faq-chevron" />
                    </button>
                    <p className="settings-faq-answer">You can cancel your subscription at any time from the Current Plan section. Your access will continue until the end of your current billing period.</p>
                  </div>
                  <div className="settings-faq-item">
                    <button className="settings-faq-question">
                      <h3 className="settings-faq-question-text">Can I get a refund?</h3>
                      <IoChevronDown className="settings-faq-chevron" />
                    </button>
                    <p className="settings-faq-answer">We offer a 14-day money-back guarantee for new subscriptions. Contact support@collegehub.com for refund requests.</p>
                  </div>
                  <div className="settings-faq-item">
                    <button className="settings-faq-question">
                      <h3 className="settings-faq-question-text">What payment methods do you accept?</h3>
                      <IoChevronDown className="settings-faq-chevron" />
                    </button>
                    <p className="settings-faq-answer">We accept all major credit cards (Visa, Mastercard, American Express, Discover) and PayPal. Payments are processed securely through Stripe.</p>
                  </div>
                  <div className="settings-faq-item">
                    <button className="settings-faq-question">
                      <h3 className="settings-faq-question-text">How do I upgrade or downgrade my plan?</h3>
                      <IoChevronDown className="settings-faq-chevron" />
                    </button>
                    <p className="settings-faq-answer">Click "Change Plan" in the Current Plan section. Upgrades take effect immediately, while downgrades apply at the next billing cycle.</p>
                  </div>
                  <div className="settings-faq-item">
                    <button className="settings-faq-question">
                      <h3 className="settings-faq-question-text">Do you offer student discounts?</h3>
                      <IoChevronDown className="settings-faq-chevron" />
                    </button>
                    <p className="settings-faq-answer">Yes! Students can get 15% off any plan with a valid .edu email address. Contact support to verify your student status.</p>
                  </div>
                  <div className="settings-faq-item">
                    <button className="settings-faq-question">
                      <h3 className="settings-faq-question-text">What happens if my payment fails?</h3>
                      <IoChevronDown className="settings-faq-chevron" />
                    </button>
                    <p className="settings-faq-answer">We'll send you an email notification and retry the payment 3 times over 7 days. Update your payment method to avoid service interruption.</p>
                  </div>
                </div>
              </div>

              {/* Need Help Section */}
              <div className="settings-billing-section">
                <div className="settings-help-card">
                  <div>
                    <h2 className="settings-help-title">Need Help with Billing?</h2>
                    <p className="settings-help-description">Our support team is here to assist you with any billing questions or concerns.</p>
                    <div className="settings-help-contact">
                      <div className="settings-help-contact-item">
                        <IoMail className="settings-help-contact-icon" />
                        <span>billing@collegehub.com</span>
                      </div>
                      <div className="settings-help-contact-item">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                        </svg>
                        <span>1-800-COLLEGE</span>
                      </div>
                    </div>
                  </div>
                  <button className="settings-button-white">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                      <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                    </svg>
                    Contact Support
                  </button>
                </div>
              </div>
            </>
          ) : activeTab === 'notifications' ? (
            <>
              {/* Notifications Section Header */}
              <div className="settings-section-header">
                <h2 className="settings-section-title">Notification Preferences</h2>
                <p className="settings-section-description">Control how and when you receive notifications</p>
              </div>

              {/* Email Notifications */}
              <div className="settings-card">
                <h3 className="settings-card-title">Email Notifications</h3>
                <div className="settings-notifications-list">
                  <div className="settings-notification-item">
                    <div className="settings-notification-content">
                      <h4 className="settings-notification-title">Application Deadlines</h4>
                      <p className="settings-notification-description">Get reminders about upcoming application deadlines</p>
                    </div>
                    <label className="settings-toggle-switch">
                      <input type="checkbox" className="settings-toggle-input" defaultChecked />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  </div>
                  <div className="settings-notification-item">
                    <div className="settings-notification-content">
                      <h4 className="settings-notification-title">Admission Updates</h4>
                      <p className="settings-notification-description">Receive updates about your application status</p>
                    </div>
                    <label className="settings-toggle-switch">
                      <input type="checkbox" className="settings-toggle-input" defaultChecked />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  </div>
                  <div className="settings-notification-item">
                    <div className="settings-notification-content">
                      <h4 className="settings-notification-title">Document Requests</h4>
                      <p className="settings-notification-description">Notifications when schools request additional documents</p>
                    </div>
                    <label className="settings-toggle-switch">
                      <input type="checkbox" className="settings-toggle-input" defaultChecked />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  </div>
                  <div className="settings-notification-item">
                    <div className="settings-notification-content">
                      <h4 className="settings-notification-title">Recommendation Updates</h4>
                      <p className="settings-notification-description">Know when teachers submit recommendation letters</p>
                    </div>
                    <label className="settings-toggle-switch">
                      <input type="checkbox" className="settings-toggle-input" defaultChecked />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  </div>
                  <div className="settings-notification-item">
                    <div className="settings-notification-content">
                      <h4 className="settings-notification-title">AI Review Results</h4>
                      <p className="settings-notification-description">Get notified when AI essay reviews are complete</p>
                    </div>
                    <label className="settings-toggle-switch">
                      <input type="checkbox" className="settings-toggle-input" defaultChecked />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  </div>
                  <div className="settings-notification-item">
                    <div className="settings-notification-content">
                      <h4 className="settings-notification-title">Weekly Summary</h4>
                      <p className="settings-notification-description">Receive a weekly digest of your application progress</p>
                    </div>
                    <label className="settings-toggle-switch">
                      <input type="checkbox" className="settings-toggle-input" defaultChecked />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  </div>
                  <div className="settings-notification-item">
                    <div className="settings-notification-content">
                      <h4 className="settings-notification-title">Marketing & Promotions</h4>
                      <p className="settings-notification-description">Receive tips, guides, and special offers</p>
                    </div>
                    <label className="settings-toggle-switch">
                      <input type="checkbox" className="settings-toggle-input" />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>

              {/* SMS Notifications */}
              <div className="settings-card">
                <div className="settings-notification-header">
                  <div>
                    <h3 className="settings-card-title">SMS Notifications</h3>
                    <p className="settings-card-description">Receive text messages for urgent updates</p>
                  </div>
                  <label className="settings-toggle-switch">
                    <input type="checkbox" className="settings-toggle-input" defaultChecked />
                    <span className="settings-toggle-slider"></span>
                  </label>
                </div>
                <div className="settings-notifications-list">
                  <div className="settings-notification-item">
                    <div className="settings-notification-content">
                      <h4 className="settings-notification-title">Urgent Deadlines</h4>
                      <p className="settings-notification-description">Text alerts for deadlines within 48 hours</p>
                    </div>
                    <label className="settings-toggle-switch">
                      <input type="checkbox" className="settings-toggle-input" defaultChecked />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  </div>
                  <div className="settings-notification-item">
                    <div className="settings-notification-content">
                      <h4 className="settings-notification-title">Decision Notifications</h4>
                      <p className="settings-notification-description">Get instant alerts when decisions are released</p>
                    </div>
                    <label className="settings-toggle-switch">
                      <input type="checkbox" className="settings-toggle-input" defaultChecked />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  </div>
                  <div className="settings-notification-item">
                    <div className="settings-notification-content">
                      <h4 className="settings-notification-title">Document Alerts</h4>
                      <p className="settings-notification-description">Urgent notifications for missing documents</p>
                    </div>
                    <label className="settings-toggle-switch">
                      <input type="checkbox" className="settings-toggle-input" defaultChecked />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  </div>
                </div>
                <div className="settings-info-box">
                  <svg className="w-5 h-5" style={{ color: '#6B7AE8' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="settings-info-title">SMS Notification Rates</h4>
                    <p className="settings-info-text">Standard messaging rates may apply. You can opt out at any time by replying STOP to any message.</p>
                  </div>
                </div>
              </div>

              {/* Push Notifications */}
              <div className="settings-card">
                <div className="settings-notification-header">
                  <div>
                    <h3 className="settings-card-title">Push Notifications</h3>
                    <p className="settings-card-description">Browser and mobile app notifications</p>
                  </div>
                  <label className="settings-toggle-switch">
                    <input type="checkbox" className="settings-toggle-input" defaultChecked />
                    <span className="settings-toggle-slider"></span>
                  </label>
                </div>
                <div className="settings-notifications-list">
                  <div className="settings-notification-item">
                    <div className="settings-notification-content">
                      <h4 className="settings-notification-title">Real-time Updates</h4>
                      <p className="settings-notification-description">Instant notifications for all activities</p>
                    </div>
                    <label className="settings-toggle-switch">
                      <input type="checkbox" className="settings-toggle-input" defaultChecked />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  </div>
                  <div className="settings-notification-item">
                    <div className="settings-notification-content">
                      <h4 className="settings-notification-title">Message Notifications</h4>
                      <p className="settings-notification-description">Alerts for new messages from schools or counselors</p>
                    </div>
                    <label className="settings-toggle-switch">
                      <input type="checkbox" className="settings-toggle-input" defaultChecked />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  </div>
                  <div className="settings-notification-item">
                    <div className="settings-notification-content">
                      <h4 className="settings-notification-title">Play Sound</h4>
                      <p className="settings-notification-description">Play notification sound with alerts</p>
                    </div>
                    <label className="settings-toggle-switch">
                      <input type="checkbox" className="settings-toggle-input" defaultChecked />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Notification Schedule */}
              <div className="settings-card">
                <h3 className="settings-card-title">Notification Schedule</h3>
                <div className="settings-schedule-grid">
                  <div>
                    <label className="settings-label">Quiet Hours</label>
                    <p className="settings-label-hint">Pause non-urgent notifications during these hours</p>
                    <div className="settings-time-inputs">
                      <div className="settings-time-input-group">
                        <label className="settings-time-label">From</label>
                        <input type="time" defaultValue="22:00" className="settings-time-input" />
                      </div>
                      <div className="settings-time-input-group">
                        <label className="settings-time-label">To</label>
                        <input type="time" defaultValue="07:00" className="settings-time-input" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="settings-label">Digest Frequency</label>
                    <p className="settings-label-hint">How often to receive summary emails</p>
                    <select className="settings-select">
                      <option>Daily (8:00 AM)</option>
                      <option>Every 3 days</option>
                      <option>Weekly (Monday 8:00 AM)</option>
                      <option>Never</option>
                    </select>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
          {/* Profile */}
          <div className="settings-card dark:bg-gray-800 dark:border-gray-700">
            <div 
              className="settings-card-header" 
              onClick={() => toggleSection('profile-personal')}
            >
              <div>
                <h2 className="settings-card-title dark:text-gray-100">Profile</h2>
                <p className="settings-card-description dark:text-gray-400">Complete your profile information for college applications</p>
              </div>
              <IoChevronDown 
                className={`settings-chevron dark:text-gray-400 ${expandedSections['profile-personal'] ? 'expanded' : ''}`}
              />
            </div>
            {expandedSections['profile-personal'] && (
              <div className="settings-card-content">
                {/* Profile Photo */}
                <div className="settings-section-divider dark:border-gray-700">
                  <h3 className="settings-section-title dark:text-gray-100">Profile Photo</h3>
                  <div className="settings-profile-photo-container">
                    <div className="settings-profile-photo-wrapper">
                      <img 
                        alt="Profile" 
                        className="settings-profile-photo"
                        src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-1.jpg"
                      />
                      <button className="settings-camera-button dark:bg-blue-600 dark:hover:bg-blue-700">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    <div className="settings-profile-photo-info">
                      <h4 className="settings-profile-photo-title dark:text-gray-100">Update Profile Picture</h4>
                      <p className="settings-profile-photo-text dark:text-gray-300">Upload a professional photo that represents you. Recommended size: 400x400px, max 5MB</p>
                      <div className="settings-profile-photo-buttons">
                        <button className="settings-button-primary dark:bg-blue-600 dark:hover:bg-blue-700">
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          Upload New Photo
                        </button>
                        <button className="settings-button-secondary dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Common App Integration */}
                <div className="settings-section-divider dark:border-gray-700">
                  <h3 className="settings-section-title dark:text-gray-100">Common App Integration</h3>
                  <div className="settings-platform-links">
                    <div className="settings-platform-link-item">
                      <div className="settings-platform-link-content">
                        <div className="settings-platform-link-icon" style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}>
                          <img 
                            src="https://res.cloudinary.com/dhleshvte/image/upload/v1765477806/Screenshot_2025-12-11_at_1.28.28_PM_pmqchn.png"
                            alt="Common App Logo"
                            className="w-6 h-6 object-cover rounded"
                          />
                        </div>
                        <div>
                          <h4 className="settings-platform-link-title dark:text-gray-100">Import from Common App</h4>
                          <p className="settings-platform-link-url dark:text-gray-400">Automatically import your profile data, test scores, activities, and college list from Common Application</p>
                          <div className="settings-platform-link-features">
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              ✓ Personal Information ✓ Test Scores ✓ Activities ✓ College List
                            </span>
                          </div>
                        </div>
                      </div>
                      <button 
                        onClick={() => setShowCommonAppModal(true)}
                        className="settings-button-primary dark:bg-blue-600 dark:hover:bg-blue-700"
                      >
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        Import Data
                      </button>
                    </div>
                  </div>
                </div>

                {/* Legal Name */}
                <div className="settings-section">
                  <h3 className="settings-section-title dark:text-gray-100">Legal Name</h3>
                  <div className="settings-form-grid">
                    <div>
                      <label className="settings-label dark:text-gray-200">Legal First Name</label>
                      <input type="text" value={profile?.first_name || ''} className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" readOnly />
                    </div>
                    <div>
                      <label className="settings-label dark:text-gray-200">Middle Name</label>
                      <input type="text" value={profile?.middle_name || ''} className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" readOnly />
                    </div>
                    <div>
                      <label className="settings-label dark:text-gray-200">Last Name</label>
                      <input type="text" value={profile?.last_name || ''} className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" readOnly />
                    </div>
                    <div>
                      <label className="settings-label dark:text-gray-200">Suffix</label>
                      <select className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200">
                        <option value="">None</option>
                        <option>Jr.</option>
                        <option>Sr.</option>
                        <option>II</option>
                        <option>III</option>
                        <option>IV</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="settings-toggle-item">
                    <div className="settings-toggle-content">
                      <p className="settings-toggle-title dark:text-gray-200">Preferred Name</p>
                      <p className="settings-toggle-description dark:text-gray-400">Do you have a preferred name different from your legal name?</p>
                    </div>
                    <label className="settings-toggle-switch">
                      <input 
                        type="checkbox" 
                        className="settings-toggle-input" 
                        checked={hasPreferredName}
                        onChange={(e) => setHasPreferredName(e.target.checked)}
                      />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  </div>

                  {hasPreferredName && (
                    <div className="settings-form-section mt-4">
                      <label className="settings-label dark:text-gray-200">Preferred Name</label>
                      <input 
                        type="text" 
                        placeholder="Enter your preferred name..." 
                        className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                      />
                    </div>
                  )}

                  <div className="settings-toggle-item">
                    <div className="settings-toggle-content">
                      <p className="settings-toggle-title dark:text-gray-200">Former Legal Name</p>
                      <p className="settings-toggle-description dark:text-gray-400">Do you have materials under a former legal name?</p>
                    </div>
                    <label className="settings-toggle-switch">
                      <input 
                        type="checkbox" 
                        className="settings-toggle-input" 
                        checked={hasFormerLegalName}
                        onChange={(e) => setHasFormerLegalName(e.target.checked)}
                      />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  </div>

                  {hasFormerLegalName && (
                    <>
                      <div className="settings-form-section mt-4">
                        <label className="settings-label dark:text-gray-200">Former Legal Name</label>
                        <input 
                          type="text" 
                          placeholder="Enter your former legal name..." 
                          className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                        />
                      </div>
                      <div className="settings-form-section">
                        <label className="settings-label dark:text-gray-200">Explanation</label>
                        <textarea 
                          rows={2} 
                          placeholder="If you have transcripts, test scores, or other materials under a different name, please explain..." 
                          className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Personal Information */}
                <div className="settings-section">
                  <h3 className="settings-section-title dark:text-gray-100">Personal Information</h3>
                  <div className="settings-form-grid">
                    <div>
                      <label className="settings-label dark:text-gray-200">Date of Birth</label>
                      <input 
                        type="date" 
                        value={profile?.date_of_birth || ''} 
                        onChange={(e) => {
                          // Update profile with new birthdate
                          if (profile) {
                            updateProfile({ date_of_birth: e.target.value });
                          }
                          setHasUnsavedChanges(true);
                        }}
                        className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" 
                      />
                    </div>
                    <div>
                      <label className="settings-label dark:text-gray-200">Phone Number</label>
                      <input 
                        type="tel" 
                        value={phoneNumber}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        placeholder="e.g., +1 (555) 123-4567 or +44 20 7946 0958"
                        className={`settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${
                          phoneError ? 'border-red-500 dark:border-red-500' : ''
                        }`}
                      />
                      {phoneError && (
                        <p className="settings-form-error dark:text-red-400 text-red-500 text-sm mt-1">{phoneError}</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <label className="settings-label dark:text-gray-200">Home Address</label>
                      {!isEditingHomeAddress ? (
                        <div className="settings-address-preview">
                          <div className="settings-address-display dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200">
                            {formatAddress(homeAddress)}
                          </div>
                          <button 
                            onClick={() => setIsEditingHomeAddress(true)}
                            className="settings-button-outline dark:bg-gray-700 dark:border-blue-500 dark:text-blue-400 dark:hover:bg-gray-600 ml-2"
                          >
                            Edit
                          </button>
                        </div>
                      ) : (
                        <div className="settings-address-form">
                          <div className="settings-form-grid">
                            <div className="col-span-2">
                              <label className="settings-label-small dark:text-gray-300">Street Address</label>
                              <input 
                                type="text" 
                                value={homeAddress.street}
                                onChange={(e) => setHomeAddress({...homeAddress, street: e.target.value})}
                                placeholder="123 Main Street"
                                className="settings-input-small dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" 
                              />
                            </div>
                            <div>
                              <label className="settings-label-small dark:text-gray-300">City</label>
                              <input 
                                type="text" 
                                value={homeAddress.city}
                                onChange={(e) => setHomeAddress({...homeAddress, city: e.target.value})}
                                placeholder="City"
                                className="settings-input-small dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" 
                              />
                            </div>
                            <div>
                              <label className="settings-label-small dark:text-gray-300">{getStateLabel(homeAddress.country)}</label>
                              <input 
                                type="text" 
                                value={homeAddress.state}
                                onChange={(e) => setHomeAddress({...homeAddress, state: e.target.value})}
                                placeholder={homeAddress.country === 'United States' ? 'CA' : 'State/Province'}
                                className="settings-input-small dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" 
                              />
                            </div>
                            <div>
                              <label className="settings-label-small dark:text-gray-300">{getPostalCodeLabel(homeAddress.country)}</label>
                              <input 
                                type="text" 
                                value={homeAddress.postalCode}
                                onChange={(e) => setHomeAddress({...homeAddress, postalCode: e.target.value})}
                                placeholder={homeAddress.country === 'United States' ? '90210' : 'Postal Code'}
                                className="settings-input-small dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" 
                              />
                            </div>
                            <div>
                              <label className="settings-label-small dark:text-gray-300">Country</label>
                              <select 
                                value={homeAddress.country}
                                onChange={(e) => setHomeAddress({...homeAddress, country: e.target.value})}
                                className="settings-input-small dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                {countries.map(country => (
                                  <option key={country} value={country}>{country}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="settings-address-actions">
                            <button 
                              onClick={() => setIsEditingHomeAddress(false)}
                              className="settings-button-primary dark:bg-blue-600 dark:hover:bg-blue-700"
                            >
                              Save
                            </button>
                            <button 
                              onClick={() => setIsEditingHomeAddress(false)}
                              className="settings-button-secondary dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="settings-toggle-item">
                    <div className="settings-toggle-content">
                      <p className="settings-toggle-title dark:text-gray-200">Separate Mailing Address</p>
                      <p className="settings-toggle-description dark:text-gray-400">Do you have a different mailing address?</p>
                    </div>
                    <label className="settings-toggle-switch">
                      <input 
                        type="checkbox" 
                        className="settings-toggle-input" 
                        checked={hasSeparateMailingAddress}
                        onChange={(e) => setHasSeparateMailingAddress(e.target.checked)}
                      />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  </div>

                  {hasSeparateMailingAddress && (
                    <div className="settings-form-section mt-4">
                      <label className="settings-label dark:text-gray-200">Mailing Address</label>
                      {!isEditingMailingAddress ? (
                        <div className="settings-address-preview">
                          <div className="settings-address-display dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200">
                            {formatAddress(mailingAddress)}
                          </div>
                          <button 
                            onClick={() => setIsEditingMailingAddress(true)}
                            className="settings-button-outline dark:bg-gray-700 dark:border-blue-500 dark:text-blue-400 dark:hover:bg-gray-600 ml-2"
                          >
                            Edit
                          </button>
                        </div>
                      ) : (
                        <div className="settings-address-form">
                          <div className="settings-form-grid">
                            <div className="col-span-2">
                              <label className="settings-label-small dark:text-gray-300">Street Address</label>
                              <input 
                                type="text" 
                                value={mailingAddress.street}
                                onChange={(e) => setMailingAddress({...mailingAddress, street: e.target.value})}
                                placeholder="123 Main Street"
                                className="settings-input-small dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" 
                              />
                            </div>
                            <div>
                              <label className="settings-label-small dark:text-gray-300">City</label>
                              <input 
                                type="text" 
                                value={mailingAddress.city}
                                onChange={(e) => setMailingAddress({...mailingAddress, city: e.target.value})}
                                placeholder="City"
                                className="settings-input-small dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" 
                              />
                            </div>
                            <div>
                              <label className="settings-label-small dark:text-gray-300">{getStateLabel(mailingAddress.country)}</label>
                              <input 
                                type="text" 
                                value={mailingAddress.state}
                                onChange={(e) => setMailingAddress({...mailingAddress, state: e.target.value})}
                                placeholder={mailingAddress.country === 'United States' ? 'CA' : 'State/Province'}
                                className="settings-input-small dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" 
                              />
                            </div>
                            <div>
                              <label className="settings-label-small dark:text-gray-300">{getPostalCodeLabel(mailingAddress.country)}</label>
                              <input 
                                type="text" 
                                value={mailingAddress.postalCode}
                                onChange={(e) => setMailingAddress({...mailingAddress, postalCode: e.target.value})}
                                placeholder={mailingAddress.country === 'United States' ? '90210' : 'Postal Code'}
                                className="settings-input-small dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" 
                              />
                            </div>
                            <div>
                              <label className="settings-label-small dark:text-gray-300">Country</label>
                              <select 
                                value={mailingAddress.country}
                                onChange={(e) => setMailingAddress({...mailingAddress, country: e.target.value})}
                                className="settings-input-small dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                {countries.map(country => (
                                  <option key={country} value={country}>{country}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="settings-address-actions">
                            <button 
                              onClick={() => setIsEditingMailingAddress(false)}
                              className="settings-button-primary dark:bg-blue-600 dark:hover:bg-blue-700"
                            >
                              Save
                            </button>
                            <button 
                              onClick={() => setIsEditingMailingAddress(false)}
                              className="settings-button-secondary dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}


                </div>

                {/* Gender and Identity */}
                <div className="settings-section">
                  <h3 className="settings-section-title dark:text-gray-100">Gender and Identity</h3>
                  <div className="settings-form-grid">
                    <div>
                      <label className="settings-label dark:text-gray-200">Gender</label>
                      <select 
                        className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                        value={selectedGender}
                        onChange={(e) => {
                          setSelectedGender(e.target.value);
                          setHasOtherGender(e.target.value === 'I identify as another gender');
                        }}
                      >
                        <option>Female</option>
                        <option>Male</option>
                        <option>Non-binary</option>
                        <option>I identify as another gender</option>
                      </select>
                      {hasOtherGender && (
                        <div className="settings-form-section mt-2">
                          <input 
                            type="text" 
                            placeholder="Please specify your gender identity..." 
                            className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="settings-label dark:text-gray-200">Pronouns</label>
                      <select 
                        className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                        value={selectedPronouns}
                        onChange={(e) => {
                          setSelectedPronouns(e.target.value);
                          setHasOtherPronouns(e.target.value === 'I go by other pronouns');
                        }}
                      >
                        <option>She/Her</option>
                        <option>He/Him</option>
                        <option>They/Them</option>
                        <option>I go by other pronouns</option>
                      </select>
                      {hasOtherPronouns && (
                        <div className="settings-form-section mt-2">
                          <input 
                            type="text" 
                            placeholder="Please specify your pronouns..." 
                            className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="settings-form-section mt-6">
                    <label className="settings-label dark:text-gray-200">Legal Sex</label>
                    <select className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200">
                      <option>Female</option>
                      <option>Male</option>
                      <option>X or another legal sex</option>
                    </select>
                  </div>
                </div>

                {/* Military Service */}
                <div className="settings-section">
                  <h3 className="settings-section-title dark:text-gray-100">Background</h3>
                  <div className="settings-form-section">
                    <label className="settings-label dark:text-gray-200">Military Service Status</label>
                    <select 
                      className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                      value={militaryStatus}
                      onChange={(e) => {
                        setMilitaryStatus(e.target.value);
                        setShowMilitaryEnrollmentStatus(e.target.value === 'Currently serving');
                      }}
                    >
                      <option>None</option>
                      <option>Currently serving</option>
                      <option>Previously served</option>
                      <option>One or more of my primary caregivers is serving currently</option>
                    </select>
                  </div>
                  
                  {showMilitaryEnrollmentStatus && (
                    <div className="settings-form-section mt-4">
                      <label className="settings-label dark:text-gray-200">Expected Status At Time of Enrollment</label>
                      <select className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200">
                        <option value="">Select status</option>
                        <option>Active Duty</option>
                        <option>Veteran</option>
                        <option>U.S. Reserves or National Guard</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Ethnicity */}
                <div className="settings-section">
                  <h3 className="settings-section-title dark:text-gray-100">Ethnicity</h3>
                  <div className="settings-form-section">
                    <label className="settings-label dark:text-gray-200">Ethnicity (Select all that apply)</label>
                    <div className="settings-checkbox-grid">
                      <label className="settings-checkbox-label dark:text-gray-200">
                        <input 
                          type="checkbox" 
                          className="settings-checkbox"
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            if (isChecked) {
                              setSelectedEthnicities([...selectedEthnicities, 'White']);
                            } else {
                              setSelectedEthnicities(selectedEthnicities.filter(eth => eth !== 'White'));
                            }
                          }}
                        />
                        <span>White</span>
                      </label>
                      <label className="settings-checkbox-label dark:text-gray-200">
                        <input 
                          type="checkbox" 
                          className="settings-checkbox"
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setShowLatinoNationality(isChecked);
                            if (isChecked) {
                              setSelectedEthnicities([...selectedEthnicities, 'Latino/a/x']);
                            } else {
                              setSelectedEthnicities(selectedEthnicities.filter(eth => eth !== 'Latino/a/x'));
                            }
                          }}
                        />
                        <span>Latino/a/x</span>
                      </label>
                      <label className="settings-checkbox-label dark:text-gray-200">
                        <input 
                          type="checkbox" 
                          className="settings-checkbox"
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            if (isChecked) {
                              setSelectedEthnicities([...selectedEthnicities, 'Black or African American']);
                            } else {
                              setSelectedEthnicities(selectedEthnicities.filter(eth => eth !== 'Black or African American'));
                            }
                          }}
                        />
                        <span>Black or African American</span>
                      </label>
                      <label className="settings-checkbox-label dark:text-gray-200">
                        <input 
                          type="checkbox" 
                          className="settings-checkbox"
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            if (isChecked) {
                              setSelectedEthnicities([...selectedEthnicities, 'Asian']);
                            } else {
                              setSelectedEthnicities(selectedEthnicities.filter(eth => eth !== 'Asian'));
                            }
                          }}
                        />
                        <span>Asian</span>
                      </label>
                      <label className="settings-checkbox-label dark:text-gray-200">
                        <input 
                          type="checkbox" 
                          className="settings-checkbox"
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            if (isChecked) {
                              setSelectedEthnicities([...selectedEthnicities, 'American Indian or Alaska Native']);
                            } else {
                              setSelectedEthnicities(selectedEthnicities.filter(eth => eth !== 'American Indian or Alaska Native'));
                            }
                          }}
                        />
                        <span>American Indian or Alaska Native</span>
                      </label>
                      <label className="settings-checkbox-label dark:text-gray-200">
                        <input 
                          type="checkbox" 
                          className="settings-checkbox"
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            if (isChecked) {
                              setSelectedEthnicities([...selectedEthnicities, 'Native Hawaiian or Other Pacific Islander']);
                            } else {
                              setSelectedEthnicities(selectedEthnicities.filter(eth => eth !== 'Native Hawaiian or Other Pacific Islander'));
                            }
                          }}
                        />
                        <span>Native Hawaiian or Other Pacific Islander</span>
                      </label>
                    </div>
                  </div>
                  
                  {showLatinoNationality && (
                    <div className="settings-form-section mt-4">
                      <label className="settings-label dark:text-gray-200">Latino/a/x Nationality</label>
                      <input type="text" placeholder="e.g., Mexican, Puerto Rican, Cuban..." className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" />
                    </div>
                  )}
                </div>

                {/* Language Information */}
                <div className="settings-section">
                  <h3 className="settings-section-title dark:text-gray-100">Language Information</h3>
                  <div className="settings-form-grid">
                    <div>
                      <label className="settings-label dark:text-gray-200">First Language</label>
                      <select className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200">
                        {allLanguages.map(language => (
                          <option key={language} value={language}>{language}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="settings-label dark:text-gray-200">Proficiency Level</label>
                      <select className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200">
                        <option>Native/Fluent</option>
                        <option>Advanced</option>
                        <option>Intermediate</option>
                        <option>Beginner</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="settings-form-section mt-6">
                    <label className="settings-label dark:text-gray-200">Do you know any other languages?</label>
                    <div className="settings-multiselect-container">
                      <div 
                        className="settings-multiselect-display dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                        onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                      >
                        <div className="settings-multiselect-tags">
                          {selectedLanguages.length > 0 ? (
                            selectedLanguages.map(language => (
                              <span key={language} className="settings-multiselect-tag dark:bg-blue-600 dark:text-white">
                                {language}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleLanguage(language);
                                  }}
                                  className="settings-multiselect-tag-remove"
                                >
                                  ×
                                </button>
                              </span>
                            ))
                          ) : (
                            <span className="settings-multiselect-placeholder dark:text-gray-400">Select languages...</span>
                          )}
                        </div>
                        <svg className={`settings-multiselect-arrow ${showLanguageDropdown ? 'rotated' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                      
                      {showLanguageDropdown && (
                        <div className="settings-multiselect-dropdown dark:bg-gray-700 dark:border-gray-600">
                          <div className="settings-multiselect-search">
                            <input
                              type="text"
                              placeholder="Search languages..."
                              className="settings-multiselect-search-input dark:bg-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                              onChange={(e) => {
                                // Filter languages based on search - for now just show all
                              }}
                            />
                          </div>
                          <div className="settings-multiselect-options">
                            {allLanguages.map(language => (
                              <div
                                key={language}
                                className={`settings-multiselect-option ${selectedLanguages.includes(language) ? 'selected' : ''} dark:text-gray-200 dark:hover:bg-gray-600`}
                                onClick={() => toggleLanguage(language)}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedLanguages.includes(language)}
                                  onChange={() => {}} // Handled by parent onClick
                                  className="settings-multiselect-checkbox"
                                />
                                <span>{language}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Dynamic Proficiency Questions */}
                  {selectedLanguages.map(language => (
                    <div key={language} className="settings-form-section">
                      <label className="settings-label dark:text-gray-200">How proficient are you in {language}?</label>
                      <div className="settings-proficiency-grid">
                        {['First Language', 'Speak', 'Read', 'Write', 'Spoken At Home'].map(proficiency => (
                          <label key={proficiency} className="settings-proficiency-label dark:text-gray-200">
                            <input
                              type="checkbox"
                              checked={languageProficiencies[language]?.includes(proficiency) || false}
                              onChange={() => toggleLanguageProficiency(language, proficiency)}
                              className="settings-proficiency-checkbox"
                            />
                            <span>{proficiency}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Birth and Citizenship */}
                <div className="settings-section">
                  <h3 className="settings-section-title dark:text-gray-100">Birth and Citizenship Information</h3>
                  <div className="settings-form-grid">
                    <div>
                      <label className="settings-label dark:text-gray-200">Birth Country</label>
                      <select className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200">
                        <option value="">Select a country...</option>
                        {allCountries.map(country => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="settings-label dark:text-gray-200">City of Birth</label>
                      <input type="text" placeholder="Enter city of birth" className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" />
                    </div>
                    <div>
                      <label className="settings-label dark:text-gray-200">Years Lived in US</label>
                      <input 
                        type="number" 
                        min="0" 
                        max="100" 
                        placeholder="Enter number of years" 
                        className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" 
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <label className="settings-label dark:text-gray-200">Citizenship Status</label>
                        <div className="relative">
                          <button
                            type="button"
                            onMouseEnter={() => setShowCitizenshipTooltip(true)}
                            onMouseLeave={() => setShowCitizenshipTooltip(false)}
                            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                          >
                            <IoInformationCircleOutline className="w-4 h-4" />
                          </button>
                          {showCitizenshipTooltip && (
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-96 p-4 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-50 dark:bg-gray-700">
                              <div className="font-semibold mb-3">Citizenship Status Definitions:</div>
                              <div className="space-y-2">
                                <div>
                                  <span className="font-medium">U.S. citizen or U.S. national:</span> Born in the U.S. or naturalized citizen, including U.S. nationals from territories
                                </div>
                                <div>
                                  <span className="font-medium">U.S. dual citizen:</span> Holds citizenship in both the U.S. and another country
                                </div>
                                <div>
                                  <span className="font-medium">U.S. permanent resident:</span> Green card holder with lawful permanent residence
                                </div>
                                <div>
                                  <span className="font-medium">Citizen of non-U.S. country:</span> International students and foreign nationals
                                </div>
                                <div>
                                  <span className="font-medium">U.S. resident:</span> Other U.S. residents including refugees, asylees, DACA, DED, TPS, undocumented students, or those with pending status
                                </div>
                              </div>
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                            </div>
                          )}
                        </div>
                      </div>
                      <select 
                        className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                        value={citizenshipStatus}
                        onChange={(e) => handleCitizenshipStatusChange(e.target.value)}
                      >
                        <option value="">Select citizenship status...</option>
                        <option value="us-citizen-national">U.S. citizen or U.S. national</option>
                        <option value="us-dual-citizen">U.S. dual citizen</option>
                        <option value="us-permanent-resident">U.S. permanent resident (green card holder)</option>
                        <option value="non-us-citizen">Citizen of non-U.S. country</option>
                        <option value="us-resident">U.S. resident</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Family */}
          <div className="settings-card dark:bg-gray-800 dark:border-gray-700">
            <div 
              className="settings-card-header" 
              onClick={() => toggleSection('family')}
            >
              <div>
                <h3 className="settings-card-title dark:text-gray-100">Family Information</h3>
                <p className="settings-card-description dark:text-gray-400">Tell us about your family for college application requirements</p>
              </div>
              <IoChevronDown 
                className={`settings-chevron dark:text-gray-400 ${expandedSections['family'] ? 'expanded' : ''}`}
              />
            </div>
            {expandedSections['family'] && (
              <div className="settings-card-content">
                {!showFamilyResults ? (
                  // Initial state: Show family description input
                  <FamilyDescriptionBox 
                    onAnalysisComplete={() => {
                      setShowFamilyResults(true);
                      console.log('Family analysis completed');
                    }}
                  />
                ) : (
                  // Results state: Show family data and missing info prompts
                  <div className="family-results-container">
                    {/* Back to edit button */}
                    <div className="mb-4">
                      <button
                        onClick={() => setShowFamilyResults(false)}
                        className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
                      >
                        ← Start Over
                      </button>
                    </div>

                    {/* Display extracted family data */}
                    {familyData && Object.keys(familyData).length > 0 && (
                      <FamilyDataDisplay 
                        familyData={familyData}
                        onEdit={(member) => {
                          // TODO: Open family member editor modal
                          console.log('Edit family member:', member);
                        }}
                        missingInfo={missingInfo}
                        onShowMissingInfo={() => {
                          // Scroll to missing info prompts section
                          const missingInfoElement = document.querySelector('.missing-info-prompts');
                          if (missingInfoElement) {
                            missingInfoElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                        }}
                      />
                    )}
                    
                    {/* Display missing information prompts */}
                    {missingInfo && missingInfo.length > 0 && (
                      <div className="mt-6">
                        <MissingInfoPrompts missingInfo={missingInfo} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Education */}
          <div className="settings-card dark:bg-gray-800 dark:border-gray-700">
            <div 
              className="settings-card-header" 
              onClick={() => toggleSection('education')}
            >
              <div>
                <h3 className="settings-card-title dark:text-gray-100">Education</h3>
                <p className="settings-card-description dark:text-gray-400">Academic history, coursework, and educational achievements</p>
              </div>
              <IoChevronDown 
                className={`settings-chevron dark:text-gray-400 ${expandedSections['education'] ? 'expanded' : ''}`}
              />
            </div>
            {expandedSections['education'] && (
              <div className="settings-card-content">
                <EducationModule />
              </div>
            )}
          </div>

          {/* Testing */}
          <div className="settings-card dark:bg-gray-800 dark:border-gray-700">
            <div 
              className="settings-card-header" 
              onClick={() => toggleSection('testing')}
            >
              <div>
                <h3 className="settings-card-title dark:text-gray-100">Testing</h3>
                <p className="settings-card-description dark:text-gray-400">Standardized tests, advanced courses, and language proficiency assessments</p>
              </div>
              <IoChevronDown 
                className={`settings-chevron dark:text-gray-400 ${expandedSections['testing'] ? 'expanded' : ''}`}
              />
            </div>
            {expandedSections['testing'] && (
              <div className="settings-card-content">
                {/* SAT/ACT Tests */}
                <div className="settings-form-section">
                  <label className="settings-label dark:text-gray-200">Did you take the SAT or ACT?</label>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 dark:text-gray-200">
                      <input 
                        type="radio" 
                        name="standardizedTest"
                        value="SAT"
                        checked={standardizedTest === 'SAT'}
                        onChange={(e) => setStandardizedTest(e.target.value)}
                        className="settings-checkbox"
                      />
                      <span>SAT</span>
                    </label>
                    <label className="flex items-center gap-2 dark:text-gray-200">
                      <input 
                        type="radio" 
                        name="standardizedTest"
                        value="ACT"
                        checked={standardizedTest === 'ACT'}
                        onChange={(e) => setStandardizedTest(e.target.value)}
                        className="settings-checkbox"
                      />
                      <span>ACT</span>
                    </label>
                    <label className="flex items-center gap-2 dark:text-gray-200">
                      <input 
                        type="radio" 
                        name="standardizedTest"
                        value="BOTH"
                        checked={standardizedTest === 'BOTH'}
                        onChange={(e) => setStandardizedTest(e.target.value)}
                        className="settings-checkbox"
                      />
                      <span>Both</span>
                    </label>
                    <label className="flex items-center gap-2 dark:text-gray-200">
                      <input 
                        type="radio" 
                        name="standardizedTest"
                        value="NEITHER"
                        checked={standardizedTest === 'NEITHER'}
                        onChange={(e) => setStandardizedTest(e.target.value)}
                        className="settings-checkbox"
                      />
                      <span>Neither</span>
                    </label>
                  </div>
                </div>

                {/* ACT Score Details */}
                {(standardizedTest === 'ACT' || standardizedTest === 'BOTH') && (
                  <div className="settings-form-section mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="settings-label dark:text-gray-200 mb-4">ACT Scores (Highest Scores)</h4>
                    
                    {/* Scores Row */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                      <div>
                        <label className="settings-label dark:text-gray-200">Math</label>
                        <select 
                          value={actScores.math.score}
                          onChange={(e) => updateActScore('math', 'score', e.target.value)}
                          className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                        >
                          <option value="">Score</option>
                          {actScoreOptions.map(score => (
                            <option key={score} value={score.toString()}>{score}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="settings-label dark:text-gray-200">English</label>
                        <select 
                          value={actScores.english.score}
                          onChange={(e) => updateActScore('english', 'score', e.target.value)}
                          className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                        >
                          <option value="">Score</option>
                          {actScoreOptions.map(score => (
                            <option key={score} value={score.toString()}>{score}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="settings-label dark:text-gray-200">Science</label>
                        <select 
                          value={actScores.science.score}
                          onChange={(e) => updateActScore('science', 'score', e.target.value)}
                          className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                        >
                          <option value="">Score</option>
                          {actScoreOptions.map(score => (
                            <option key={score} value={score.toString()}>{score}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="settings-label dark:text-gray-200">Reading</label>
                        <select 
                          value={actScores.reading.score}
                          onChange={(e) => updateActScore('reading', 'score', e.target.value)}
                          className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                        >
                          <option value="">Score</option>
                          {actScoreOptions.map(score => (
                            <option key={score} value={score.toString()}>{score}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="settings-label dark:text-gray-200">
                          Composite <span className="text-xs text-gray-500 dark:text-gray-400">(Auto-calculated)</span>
                        </label>
                        <div className="relative">
                          <select 
                            value={actScores.composite.score}
                            onChange={(e) => updateActScore('composite', 'score', e.target.value)}
                            className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                          >
                            <option value="">Score</option>
                            {actScoreOptions.map(score => (
                              <option key={score} value={score.toString()}>{score}</option>
                            ))}
                          </select>
                          {calculateCompositeScore() && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <span className="text-blue-600 dark:text-blue-400 font-medium">
                                {calculateCompositeScore()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Dates Row */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {['math', 'english', 'science', 'reading', 'composite'].map((section) => {
                        const uniqueDates = getUniqueDates();
                        const currentDate = actScores[section as keyof typeof actScores].date;
                        const isComposite = section === 'composite';
                        
                        return (
                          <div key={section} className="relative">
                            <label className="settings-label dark:text-gray-200">
                              Date{isComposite && <span className="text-xs text-gray-500 dark:text-gray-400"> (Auto-filled)</span>}
                            </label>
                            <input 
                              type="text"
                              placeholder="MM/YYYY"
                              value={currentDate}
                              onChange={(e) => updateActScore(section, 'date', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              list={`act-dates-${section}`}
                            />
                            {uniqueDates.length > 0 && (
                              <datalist id={`act-dates-${section}`}>
                                {uniqueDates.map((date, index) => (
                                  <option key={index} value={date} />
                                ))}
                              </datalist>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Writing Score Section */}
                    <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="mb-4">
                        <label className="settings-label dark:text-gray-200">Would you like to submit a Writing score?</label>
                        <div className="flex gap-4 mt-2">
                          <label className="flex items-center gap-2 dark:text-gray-200">
                            <input 
                              type="radio" 
                              name="submitWritingScore"
                              value="yes"
                              checked={submitWritingScore === true}
                              onChange={() => setSubmitWritingScore(true)}
                              className="settings-checkbox"
                            />
                            <span>Yes</span>
                          </label>
                          <label className="flex items-center gap-2 dark:text-gray-200">
                            <input 
                              type="radio" 
                              name="submitWritingScore"
                              value="no"
                              checked={submitWritingScore === false}
                              onChange={() => setSubmitWritingScore(false)}
                              className="settings-checkbox"
                            />
                            <span>No</span>
                          </label>
                        </div>
                      </div>

                      {submitWritingScore && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="settings-label dark:text-gray-200">Writing Score (2-12)</label>
                            <select 
                              value={writingScore.score}
                              onChange={(e) => updateActScore('writing', 'score', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                            >
                              <option value="">Select...</option>
                              {actWritingScoreOptions.map(score => (
                                <option key={score} value={score.toString()}>{score}</option>
                              ))}
                            </select>
                          </div>
                          <div className="relative">
                            <label className="settings-label dark:text-gray-200">Date</label>
                            <input 
                              type="text"
                              placeholder="MM/YYYY"
                              value={writingScore.date}
                              onChange={(e) => updateActScore('writing', 'date', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              list="act-dates-writing"
                            />
                            {getUniqueDates().length > 0 && (
                              <datalist id="act-dates-writing">
                                {getUniqueDates().map((date, index) => (
                                  <option key={index} value={date} />
                                ))}
                              </datalist>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* SAT Score Details */}
                {(standardizedTest === 'SAT' || standardizedTest === 'BOTH') && (
                  <div className="settings-form-section mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="settings-label dark:text-gray-200 mb-4">SAT Scores (Highest Scores)</h4>
                    
                    {/* Scores Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <label className="settings-label dark:text-gray-200">Reading & Writing</label>
                        <select 
                          value={satScores.readingWriting.score}
                          onChange={(e) => updateSatScore('readingWriting', 'score', e.target.value)}
                          className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                        >
                          <option value="">Score</option>
                          {satScoreOptions.map(score => (
                            <option key={score} value={score.toString()}>{score}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="settings-label dark:text-gray-200">Math</label>
                        <select 
                          value={satScores.math.score}
                          onChange={(e) => updateSatScore('math', 'score', e.target.value)}
                          className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                        >
                          <option value="">Score</option>
                          {satScoreOptions.map(score => (
                            <option key={score} value={score.toString()}>{score}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="settings-label dark:text-gray-200">
                          Total <span className="text-xs text-gray-500 dark:text-gray-400">(Auto-calculated)</span>
                        </label>
                        <div className="relative">
                          <select 
                            value={satScores.total.score}
                            onChange={(e) => updateSatScore('total', 'score', e.target.value)}
                            className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                          >
                            <option value="">Score</option>
                            {Array.from({ length: 121 }, (_, i) => 400 + (i * 10)).map(score => (
                              <option key={score} value={score.toString()}>{score}</option>
                            ))}
                          </select>
                          {calculateSatTotalScore() && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <span className="text-blue-600 dark:text-blue-400 font-medium">
                                {calculateSatTotalScore()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="settings-label dark:text-gray-200">Essay</label>
                        <select 
                          value={satScores.essay.score}
                          onChange={(e) => updateSatScore('essay', 'score', e.target.value)}
                          className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                        >
                          <option value="">Score</option>
                          {satEssayScoreOptions.map(score => (
                            <option key={score} value={score.toString()}>{score}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Dates Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {['readingWriting', 'math', 'total', 'essay'].map((section) => {
                        const uniqueDates = getUniqueDates();
                        const currentDate = satScores[section as keyof typeof satScores].date;
                        const isTotal = section === 'total';
                        
                        return (
                          <div key={section} className="relative">
                            <label className="settings-label dark:text-gray-200">
                              Date{isTotal && <span className="text-xs text-gray-500 dark:text-gray-400"> (Auto-filled)</span>}
                            </label>
                            <input 
                              type="text"
                              placeholder="MM/YYYY"
                              value={currentDate}
                              onChange={(e) => updateSatScore(section, 'date', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              list={`sat-dates-${section}`}
                            />
                            {uniqueDates.length > 0 && (
                              <datalist id={`sat-dates-${section}`}>
                                {uniqueDates.map((date, index) => (
                                  <option key={index} value={date} />
                                ))}
                              </datalist>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Advanced Courses */}
                <div className="settings-form-section">
                  <label className="settings-label dark:text-gray-200">Advanced courses <span className="text-gray-500 dark:text-gray-400">(Select all that apply)</span></label>
                  <div className="flex gap-6">
                    <label className="settings-checkbox-label dark:text-gray-200">
                      <input 
                        type="checkbox" 
                        className="settings-checkbox"
                        checked={advancedCourses.includes('IB')}
                        onChange={(e) => handleAdvancedCourseChange('IB', e.target.checked)}
                      />
                      <span>IB Subject Tests</span>
                    </label>
                    <label className="settings-checkbox-label dark:text-gray-200">
                      <input 
                        type="checkbox" 
                        className="settings-checkbox"
                        checked={advancedCourses.includes('AP')}
                        onChange={(e) => handleAdvancedCourseChange('AP', e.target.checked)}
                      />
                      <span>AP Subject Tests</span>
                    </label>
                    <label className="settings-checkbox-label dark:text-gray-200">
                      <input 
                        type="checkbox" 
                        className="settings-checkbox"
                        checked={advancedCourses.includes('SAT')}
                        onChange={(e) => handleAdvancedCourseChange('SAT', e.target.checked)}
                      />
                      <span>SAT Subject Tests</span>
                    </label>
                    <label className="settings-checkbox-label dark:text-gray-200">
                      <input 
                        type="checkbox" 
                        className="settings-checkbox"
                        checked={advancedCourses.includes('Cambridge')}
                        onChange={(e) => handleAdvancedCourseChange('Cambridge', e.target.checked)}
                      />
                      <span>Cambridge Tests</span>
                    </label>
                  </div>
                </div>

                {/* AP Tests Details */}
                {advancedCourses.includes('AP') && (
                  <div className="settings-form-section mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <label className="settings-label dark:text-gray-200">How many AP scores would you like to report?</label>
                    <select 
                      value={apCount} 
                      onChange={(e) => handleCountChange('AP', parseInt(e.target.value))}
                      className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 w-32"
                    >
                      <option value={0}>Select...</option>
                      {[1,2,3,4,5,6,7,8,9,10].map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                    </select>
                    
                    {apCount > 0 && (
                      <div className="mt-4 space-y-4">
                        {apTests.map((test, index) => (
                          <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div>
                              <label className="settings-label dark:text-gray-200">Subject</label>
                              <select 
                                value={test.subject}
                                onChange={(e) => updateTestDetail('AP', index, 'subject', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Select subject...</option>
                                {apSubjects.map(subject => (
                                  <option key={subject} value={subject}>{subject}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="settings-label dark:text-gray-200">Date Taken/Planned</label>
                              <input 
                                type="text"
                                placeholder="MM/YYYY"
                                value={test.dateTaken}
                                onChange={(e) => updateTestDetail('AP', index, 'dateTaken', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              />
                            </div>
                            <div>
                              <label className="settings-label dark:text-gray-200">Score</label>
                              <select 
                                value={test.score}
                                onChange={(e) => updateTestDetail('AP', index, 'score', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Select score...</option>
                                <option value="5">5</option>
                                <option value="4">4</option>
                                <option value="3">3</option>
                                <option value="2">2</option>
                                <option value="1">1</option>
                                <option value="I don't know yet">I don't know yet</option>
                              </select>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* IB Tests Details */}
                {advancedCourses.includes('IB') && (
                  <div className="settings-form-section mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <label className="settings-label dark:text-gray-200">How many IB scores would you like to report?</label>
                    <select 
                      value={ibCount} 
                      onChange={(e) => handleCountChange('IB', parseInt(e.target.value))}
                      className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 w-32"
                    >
                      <option value={0}>Select...</option>
                      {[1,2,3,4,5,6,7,8,9,10].map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                    </select>
                    
                    {ibCount > 0 && (
                      <div className="mt-4 space-y-4">
                        {ibTests.map((test, index) => (
                          <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div>
                              <label className="settings-label dark:text-gray-200">Subject</label>
                              <select 
                                value={test.subject}
                                onChange={(e) => updateTestDetail('IB', index, 'subject', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Select subject...</option>
                                {ibSubjects.map(subject => (
                                  <option key={subject} value={subject}>{subject}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="settings-label dark:text-gray-200">Course Level</label>
                              <select 
                                value={test.level}
                                onChange={(e) => updateTestDetail('IB', index, 'level', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Select level...</option>
                                <option value="Higher Level (HL)">Higher Level (HL)</option>
                                <option value="Standard Level (SL)">Standard Level (SL)</option>
                              </select>
                            </div>
                            <div>
                              <label className="settings-label dark:text-gray-200">Date Taken/Planned</label>
                              <input 
                                type="text"
                                placeholder="MM/YYYY"
                                value={test.dateTaken}
                                onChange={(e) => updateTestDetail('IB', index, 'dateTaken', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              />
                            </div>
                            <div>
                              <label className="settings-label dark:text-gray-200">Score</label>
                              <select 
                                value={test.score}
                                onChange={(e) => updateTestDetail('IB', index, 'score', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Select score...</option>
                                <option value="7">7</option>
                                <option value="6">6</option>
                                <option value="5">5</option>
                                <option value="4">4</option>
                                <option value="3">3</option>
                                <option value="2">2</option>
                                <option value="1">1</option>
                                <option value="I don't know yet">I don't know yet</option>
                              </select>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* SAT Subject Tests Details */}
                {advancedCourses.includes('SAT') && (
                  <div className="settings-form-section mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <label className="settings-label dark:text-gray-200">How many SAT Subject Test scores would you like to report?</label>
                    <select 
                      value={satSubjectCount} 
                      onChange={(e) => handleCountChange('SAT', parseInt(e.target.value))}
                      className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 w-32"
                    >
                      <option value={0}>Select...</option>
                      {[1,2,3,4,5,6,7,8,9,10].map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                    </select>
                    
                    {satSubjectCount > 0 && (
                      <div className="mt-4 space-y-4">
                        {satSubjectTests.map((test, index) => (
                          <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div>
                              <label className="settings-label dark:text-gray-200">Subject</label>
                              <select 
                                value={test.subject}
                                onChange={(e) => updateTestDetail('SAT', index, 'subject', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Select subject...</option>
                                {satSubjectOptions.map(subject => (
                                  <option key={subject} value={subject}>{subject}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="settings-label dark:text-gray-200">Date Taken/Planned</label>
                              <input 
                                type="text"
                                placeholder="MM/YYYY"
                                value={test.dateTaken}
                                onChange={(e) => updateTestDetail('SAT', index, 'dateTaken', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              />
                            </div>
                            <div>
                              <label className="settings-label dark:text-gray-200">Score</label>
                              <select 
                                value={test.score}
                                onChange={(e) => updateTestDetail('SAT', index, 'score', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Select score...</option>
                                {satScoreOptions.map(score => (
                                  <option key={score} value={score.toString()}>{score}</option>
                                ))}
                                <option value="I don't know yet">I don't know yet</option>
                              </select>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Cambridge Tests Details */}
                {advancedCourses.includes('Cambridge') && (
                  <div className="settings-form-section mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <label className="settings-label dark:text-gray-200">How many Cambridge Test scores would you like to report?</label>
                    <select 
                      value={cambridgeCount} 
                      onChange={(e) => handleCountChange('Cambridge', parseInt(e.target.value))}
                      className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 w-32"
                    >
                      <option value={0}>Select...</option>
                      {[1,2,3,4,5,6,7,8,9,10].map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                    </select>
                    
                    {cambridgeCount > 0 && (
                      <div className="mt-4 space-y-4">
                        {cambridgeTests.map((test, index) => (
                          <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div>
                              <label className="settings-label dark:text-gray-200">Subject</label>
                              <select 
                                value={test.subject}
                                onChange={(e) => updateTestDetail('Cambridge', index, 'subject', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Select subject...</option>
                                {cambridgeSubjects.map(subject => (
                                  <option key={subject} value={subject}>{subject}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="settings-label dark:text-gray-200">Course Level</label>
                              <select 
                                value={test.level}
                                onChange={(e) => updateTestDetail('Cambridge', index, 'level', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Select level...</option>
                                <option value="Advanced (A-Level)">Advanced (A-Level)</option>
                                <option value="Advanced (AS-Level)">Advanced (AS-Level)</option>
                                <option value="Pre-Advanced (IGCSE)">Pre-Advanced (IGCSE)</option>
                              </select>
                            </div>
                            <div>
                              <label className="settings-label dark:text-gray-200">Expected Test Date</label>
                              <input 
                                type="text"
                                placeholder="MM/YYYY"
                                value={test.dateTaken}
                                onChange={(e) => updateTestDetail('Cambridge', index, 'dateTaken', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              />
                            </div>
                            <div>
                              <label className="settings-label dark:text-gray-200">Grade</label>
                              <select 
                                value={test.score}
                                onChange={(e) => updateTestDetail('Cambridge', index, 'score', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                                disabled={!test.level}
                              >
                                <option value="">Select grade...</option>
                                {test.level && getCambridgeScoreOptions(test.level).map(grade => (
                                  <option key={grade} value={grade}>{grade}</option>
                                ))}
                                <option value="I don't know yet">I don't know yet</option>
                              </select>
                              {!test.level && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  Select course level first
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Language Tests */}
                {showLanguageTest && (
                  <div className="settings-section">
                    <h3 className="settings-section-title dark:text-gray-100">Language Tests</h3>
                    <div className="settings-form-section">
                      <label className="settings-label dark:text-gray-200">Language Test Taken <span className="text-gray-500 dark:text-gray-400">(only if English is not your first language)</span></label>
                      <select 
                        value={languageTest} 
                        onChange={(e) => handleLanguageTestChange(e.target.value)}
                        className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                      >
                        <option value="">Select a language test...</option>
                        <option value="IELTS">IELTS</option>
                        <option value="PTE">PTE Academic Test</option>
                        <option value="TOEFL">TOEFL iBT</option>
                        <option value="Duolingo">Duolingo English Test</option>
                        <option value="Cambridge">Cambridge English Qualification</option>
                      </select>
                    </div>

                    {/* TOEFL iBT Scores */}
                    {languageTest === 'TOEFL' && (
                      <div className="settings-form-section mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="settings-label dark:text-gray-200 mb-4">TOEFL iBT Scores (Highest Scores)</h4>
                        
                        {/* Scores Row */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                          <div>
                            <label className="settings-label dark:text-gray-200">Reading</label>
                            <select 
                              value={toeflScores.reading.score}
                              onChange={(e) => updateToeflScore('reading', 'score', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                            >
                              <option value="">Score</option>
                              {Array.from({ length: 31 }, (_, i) => (
                                <option key={i} value={i}>{i}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label className="settings-label dark:text-gray-200">Writing</label>
                            <select 
                              value={toeflScores.writing.score}
                              onChange={(e) => updateToeflScore('writing', 'score', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                            >
                              <option value="">Score</option>
                              {Array.from({ length: 31 }, (_, i) => (
                                <option key={i} value={i}>{i}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label className="settings-label dark:text-gray-200">Speaking</label>
                            <select 
                              value={toeflScores.speaking.score}
                              onChange={(e) => updateToeflScore('speaking', 'score', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                            >
                              <option value="">Score</option>
                              {Array.from({ length: 31 }, (_, i) => (
                                <option key={i} value={i}>{i}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label className="settings-label dark:text-gray-200">Listening</label>
                            <select 
                              value={toeflScores.listening.score}
                              onChange={(e) => updateToeflScore('listening', 'score', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                            >
                              <option value="">Score</option>
                              {Array.from({ length: 31 }, (_, i) => (
                                <option key={i} value={i}>{i}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label className="settings-label dark:text-gray-200">
                              Total <span className="text-xs text-gray-500 dark:text-gray-400">(Auto-calculated)</span>
                            </label>
                            <div className="relative">
                              <select 
                                value={toeflScores.total.score}
                                onChange={(e) => updateToeflScore('total', 'score', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Score</option>
                                {Array.from({ length: 121 }, (_, i) => (
                                  <option key={i} value={i}>{i}</option>
                                ))}
                              </select>
                              {calculateToeflTotalScore() && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                  <span className="text-blue-600 dark:text-blue-400 font-medium">
                                    {calculateToeflTotalScore()}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Dates Row */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                          {['reading', 'writing', 'speaking', 'listening', 'total'].map((section) => {
                            const currentDate = toeflScores[section as keyof typeof toeflScores].date;
                            const isTotal = section === 'total';
                            
                            return (
                              <div key={section} className="relative">
                                <label className="settings-label dark:text-gray-200">
                                  Date{isTotal && <span className="text-xs text-gray-500 dark:text-gray-400"> (Auto-filled)</span>}
                                </label>
                                <input 
                                  type="text"
                                  placeholder="MM/YYYY"
                                  value={currentDate}
                                  onChange={(e) => updateToeflScore(section, 'date', e.target.value)}
                                  className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Duolingo English Test Scores */}
                    {languageTest === 'Duolingo' && (
                      <div className="settings-form-section mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="settings-label dark:text-gray-200 mb-4">Duolingo English Test Scores (Highest Scores)</h4>
                        
                        {/* Scores Row */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                          <div>
                            <label className="settings-label dark:text-gray-200">Literacy</label>
                            <select 
                              value={duolingoScores.literacy.score}
                              onChange={(e) => updateDuolingoScore('literacy', 'score', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                            >
                              <option value="">Score</option>
                              {Array.from({ length: 31 }, (_, i) => {
                                const score = 10 + (i * 5);
                                return score <= 160 ? <option key={score} value={score}>{score}</option> : null;
                              })}
                            </select>
                          </div>
                          
                          <div>
                            <label className="settings-label dark:text-gray-200">Comprehension</label>
                            <select 
                              value={duolingoScores.comprehension.score}
                              onChange={(e) => updateDuolingoScore('comprehension', 'score', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                            >
                              <option value="">Score</option>
                              {Array.from({ length: 31 }, (_, i) => {
                                const score = 10 + (i * 5);
                                return score <= 160 ? <option key={score} value={score}>{score}</option> : null;
                              })}
                            </select>
                          </div>
                          
                          <div>
                            <label className="settings-label dark:text-gray-200">Conversation</label>
                            <select 
                              value={duolingoScores.conversation.score}
                              onChange={(e) => updateDuolingoScore('conversation', 'score', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                            >
                              <option value="">Score</option>
                              {Array.from({ length: 31 }, (_, i) => {
                                const score = 10 + (i * 5);
                                return score <= 160 ? <option key={score} value={score}>{score}</option> : null;
                              })}
                            </select>
                          </div>
                          
                          <div>
                            <label className="settings-label dark:text-gray-200">Production</label>
                            <select 
                              value={duolingoScores.production.score}
                              onChange={(e) => updateDuolingoScore('production', 'score', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                            >
                              <option value="">Score</option>
                              {Array.from({ length: 31 }, (_, i) => {
                                const score = 10 + (i * 5);
                                return score <= 160 ? <option key={score} value={score}>{score}</option> : null;
                              })}
                            </select>
                          </div>
                          
                          <div>
                            <label className="settings-label dark:text-gray-200">
                              Overall <span className="text-xs text-gray-500 dark:text-gray-400">(Average)</span>
                            </label>
                            <div className="relative">
                              <select 
                                value={duolingoScores.total.score}
                                onChange={(e) => updateDuolingoScore('total', 'score', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Score</option>
                                {Array.from({ length: 31 }, (_, i) => {
                                  const score = 10 + (i * 5);
                                  return score <= 160 ? <option key={score} value={score}>{score}</option> : null;
                                })}
                              </select>
                              {calculateDuolingoTotalScore() && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                  <span className="text-blue-600 dark:text-blue-400 font-medium">
                                    {calculateDuolingoTotalScore()}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Dates Row */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                          {['literacy', 'comprehension', 'conversation', 'production', 'total'].map((section) => {
                            const currentDate = duolingoScores[section as keyof typeof duolingoScores].date;
                            const isTotal = section === 'total';
                            
                            return (
                              <div key={section} className="relative">
                                <label className="settings-label dark:text-gray-200">
                                  Date{isTotal && <span className="text-xs text-gray-500 dark:text-gray-400"> (Auto-filled)</span>}
                                </label>
                                <input 
                                  type="text"
                                  placeholder="MM/YYYY"
                                  value={currentDate}
                                  onChange={(e) => updateDuolingoScore(section, 'date', e.target.value)}
                                  className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* IELTS Scores */}
                    {languageTest === 'IELTS' && (
                      <div className="settings-form-section mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="settings-label dark:text-gray-200 mb-4">IELTS Scores (Highest Scores)</h4>
                        
                        {/* Scores Row */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                          <div>
                            <label className="settings-label dark:text-gray-200">Reading</label>
                            <select 
                              value={ieltsScores.reading.score}
                              onChange={(e) => updateIeltsScore('reading', 'score', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                            >
                              <option value="">Score</option>
                              {Array.from({ length: 19 }, (_, i) => {
                                const score = i * 0.5;
                                return <option key={score} value={score}>{score}</option>;
                              })}
                            </select>
                          </div>
                          
                          <div>
                            <label className="settings-label dark:text-gray-200">Writing</label>
                            <select 
                              value={ieltsScores.writing.score}
                              onChange={(e) => updateIeltsScore('writing', 'score', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                            >
                              <option value="">Score</option>
                              {Array.from({ length: 19 }, (_, i) => {
                                const score = i * 0.5;
                                return <option key={score} value={score}>{score}</option>;
                              })}
                            </select>
                          </div>
                          
                          <div>
                            <label className="settings-label dark:text-gray-200">Listening</label>
                            <select 
                              value={ieltsScores.listening.score}
                              onChange={(e) => updateIeltsScore('listening', 'score', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                            >
                              <option value="">Score</option>
                              {Array.from({ length: 19 }, (_, i) => {
                                const score = i * 0.5;
                                return <option key={score} value={score}>{score}</option>;
                              })}
                            </select>
                          </div>
                          
                          <div>
                            <label className="settings-label dark:text-gray-200">Speaking</label>
                            <select 
                              value={ieltsScores.speaking.score}
                              onChange={(e) => updateIeltsScore('speaking', 'score', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                            >
                              <option value="">Score</option>
                              {Array.from({ length: 19 }, (_, i) => {
                                const score = i * 0.5;
                                return <option key={score} value={score}>{score}</option>;
                              })}
                            </select>
                          </div>
                          
                          <div>
                            <label className="settings-label dark:text-gray-200">
                              Overall <span className="text-xs text-gray-500 dark:text-gray-400">(Average)</span>
                            </label>
                            <div className="relative">
                              <select 
                                value={ieltsScores.overall.score}
                                onChange={(e) => updateIeltsScore('overall', 'score', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Score</option>
                                {Array.from({ length: 19 }, (_, i) => {
                                  const score = i * 0.5;
                                  return <option key={score} value={score}>{score}</option>;
                                })}
                              </select>
                              {calculateIeltsOverallScore() && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                  <span className="text-blue-600 dark:text-blue-400 font-medium">
                                    {calculateIeltsOverallScore()}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Dates Row */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                          {['reading', 'writing', 'listening', 'speaking', 'overall'].map((section) => {
                            const currentDate = ieltsScores[section as keyof typeof ieltsScores].date;
                            const isOverall = section === 'overall';
                            
                            return (
                              <div key={section} className="relative">
                                <label className="settings-label dark:text-gray-200">
                                  Date{isOverall && <span className="text-xs text-gray-500 dark:text-gray-400"> (Auto-filled)</span>}
                                </label>
                                <input 
                                  type="text"
                                  placeholder="MM/YYYY"
                                  value={currentDate}
                                  onChange={(e) => updateIeltsScore(section, 'date', e.target.value)}
                                  className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* PTE Academic Test Scores */}
                    {languageTest === 'PTE' && (
                      <div className="settings-form-section mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="settings-label dark:text-gray-200 mb-4">PTE Academic Test Scores (Highest Scores)</h4>
                        
                        {/* First Row - Core Skills */}
                        <div className="mb-6">
                          <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Core Skills</h5>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div>
                              <label className="settings-label dark:text-gray-200">Listening</label>
                              <select 
                                value={pteScores.listening.score}
                                onChange={(e) => updatePteScore('listening', 'score', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Score</option>
                                {Array.from({ length: 81 }, (_, i) => {
                                  const score = 10 + i;
                                  return <option key={score} value={score}>{score}</option>;
                                })}
                              </select>
                            </div>
                            
                            <div>
                              <label className="settings-label dark:text-gray-200">Reading</label>
                              <select 
                                value={pteScores.reading.score}
                                onChange={(e) => updatePteScore('reading', 'score', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Score</option>
                                {Array.from({ length: 81 }, (_, i) => {
                                  const score = 10 + i;
                                  return <option key={score} value={score}>{score}</option>;
                                })}
                              </select>
                            </div>
                            
                            <div>
                              <label className="settings-label dark:text-gray-200">Speaking</label>
                              <select 
                                value={pteScores.speaking.score}
                                onChange={(e) => updatePteScore('speaking', 'score', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Score</option>
                                {Array.from({ length: 81 }, (_, i) => {
                                  const score = 10 + i;
                                  return <option key={score} value={score}>{score}</option>;
                                })}
                              </select>
                            </div>
                            
                            <div>
                              <label className="settings-label dark:text-gray-200">Writing</label>
                              <select 
                                value={pteScores.writing.score}
                                onChange={(e) => updatePteScore('writing', 'score', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Score</option>
                                {Array.from({ length: 81 }, (_, i) => {
                                  const score = 10 + i;
                                  return <option key={score} value={score}>{score}</option>;
                                })}
                              </select>
                            </div>
                          </div>
                          
                          {/* Core Skills Dates */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {['listening', 'reading', 'speaking', 'writing'].map((section) => (
                              <div key={section}>
                                <label className="settings-label dark:text-gray-200">Date</label>
                                <input 
                                  type="text"
                                  placeholder="MM/YYYY"
                                  value={pteScores[section as keyof typeof pteScores].date}
                                  onChange={(e) => updatePteScore(section, 'date', e.target.value)}
                                  className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Second Row - Enabling Skills */}
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Enabling Skills</h5>
                          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-4">
                            <div>
                              <label className="settings-label dark:text-gray-200">Grammar</label>
                              <select 
                                value={pteScores.grammar.score}
                                onChange={(e) => updatePteScore('grammar', 'score', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Score</option>
                                {Array.from({ length: 81 }, (_, i) => {
                                  const score = 10 + i;
                                  return <option key={score} value={score}>{score}</option>;
                                })}
                              </select>
                            </div>
                            
                            <div>
                              <label className="settings-label dark:text-gray-200">Oral Fluency</label>
                              <select 
                                value={pteScores.oral_fluency.score}
                                onChange={(e) => updatePteScore('oral_fluency', 'score', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Score</option>
                                {Array.from({ length: 81 }, (_, i) => {
                                  const score = 10 + i;
                                  return <option key={score} value={score}>{score}</option>;
                                })}
                              </select>
                            </div>
                            
                            <div>
                              <label className="settings-label dark:text-gray-200">Pronunciation</label>
                              <select 
                                value={pteScores.pronunciation.score}
                                onChange={(e) => updatePteScore('pronunciation', 'score', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Score</option>
                                {Array.from({ length: 81 }, (_, i) => {
                                  const score = 10 + i;
                                  return <option key={score} value={score}>{score}</option>;
                                })}
                              </select>
                            </div>
                            
                            <div>
                              <label className="settings-label dark:text-gray-200">Spelling</label>
                              <select 
                                value={pteScores.spelling.score}
                                onChange={(e) => updatePteScore('spelling', 'score', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Score</option>
                                {Array.from({ length: 81 }, (_, i) => {
                                  const score = 10 + i;
                                  return <option key={score} value={score}>{score}</option>;
                                })}
                              </select>
                            </div>
                            
                            <div>
                              <label className="settings-label dark:text-gray-200">Vocabulary</label>
                              <select 
                                value={pteScores.vocabulary.score}
                                onChange={(e) => updatePteScore('vocabulary', 'score', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Score</option>
                                {Array.from({ length: 81 }, (_, i) => {
                                  const score = 10 + i;
                                  return <option key={score} value={score}>{score}</option>;
                                })}
                              </select>
                            </div>
                            
                            <div>
                              <label className="settings-label dark:text-gray-200">Written Discourse</label>
                              <select 
                                value={pteScores.written_discourse.score}
                                onChange={(e) => updatePteScore('written_discourse', 'score', e.target.value)}
                                className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                              >
                                <option value="">Score</option>
                                {Array.from({ length: 81 }, (_, i) => {
                                  const score = 10 + i;
                                  return <option key={score} value={score}>{score}</option>;
                                })}
                              </select>
                            </div>
                          </div>
                          
                          {/* Enabling Skills Dates */}
                          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                            {['grammar', 'oral_fluency', 'pronunciation', 'spelling', 'vocabulary', 'written_discourse'].map((section) => (
                              <div key={section}>
                                <label className="settings-label dark:text-gray-200">Date</label>
                                <input 
                                  type="text"
                                  placeholder="MM/YYYY"
                                  value={pteScores[section as keyof typeof pteScores].date}
                                  onChange={(e) => updatePteScore(section, 'date', e.target.value)}
                                  className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Cambridge English Qualification */}
                    {languageTest === 'Cambridge' && (
                      <div className="settings-form-section mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="settings-label dark:text-gray-200 mb-4">Cambridge English Qualification</h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="settings-label dark:text-gray-200">Proficiency Level</label>
                            <select 
                              value={cambridgeQualification.proficiency_level}
                              onChange={(e) => updateCambridgeQualification('proficiency_level', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                            >
                              <option value="">Select Level</option>
                              <option value="C2 Proficient">C2 Proficient</option>
                              <option value="C1 Advanced">C1 Advanced</option>
                              <option value="B2 First">B2 First</option>
                              <option value="B1 Preliminary">B1 Preliminary</option>
                              <option value="A2 Key">A2 Key</option>
                              <option value="A1 Movers">A1 Movers</option>
                              <option value="Pre-A1 Starters">Pre-A1 Starters</option>
                            </select>
                          </div>
                          
                          <div>
                            <label className="settings-label dark:text-gray-200">Exam Date</label>
                            <input 
                              type="text"
                              placeholder="MM/YYYY"
                              value={cambridgeQualification.exam_date}
                              onChange={(e) => updateCambridgeQualification('exam_date', e.target.value)}
                              className="settings-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Activities */}
          <div className="settings-card dark:bg-gray-800 dark:border-gray-700">
            <div 
              className="settings-card-header" 
              onClick={() => toggleSection('activities')}
            >
              <div>
                <h3 className="settings-card-title dark:text-gray-100">Activities</h3>
                <p className="settings-card-description dark:text-gray-400">Extracurricular activities, leadership roles, and community involvement</p>
              </div>
              <IoChevronDown 
                className={`settings-chevron dark:text-gray-400 ${expandedSections['activities'] ? 'expanded' : ''}`}
              />
            </div>
            {expandedSections['activities'] && (
              <div className="settings-card-content">
                {!showActivityResults ? (
                  // Initial state: Show activity description input
                  <ActivityDescriptionBox 
                    onAnalysisComplete={() => {
                      setShowActivityResults(true);
                      console.log('Activity analysis completed');
                    }}
                    onAnalyze={async (description: string) => {
                      try {
                        console.log('Starting activity analysis for:', description);
                        const result = await analyzeActivity({ description });
                        console.log('Analysis result:', result);
                        
                        // Automatically create the activity after analysis
                        const createdActivity = await createActivity({
                          activity_type: result.activity.activity_type,
                          position_description: result.activity.position_description,
                          organization_name: result.activity.organization_name,
                          activity_description: result.activity.activity_description,
                          participation_grades: result.activity.participation_grades,
                          timing_of_participation: result.activity.timing_of_participation,
                          hours_per_week: result.activity.hours_per_week,
                          weeks_per_year: result.activity.weeks_per_year,
                          intend_to_participate_in_college: result.activity.intend_to_participate_in_college,
                          raw_description: description
                        });
                        console.log('Activity created successfully:', createdActivity);
                      } catch (error) {
                        console.error('Failed to analyze and create activity:', error);
                        throw error;
                      }
                    }}
                    isAnalyzing={isAnalyzing}
                  />
                ) : (
                  // Results state: Show activity cards and option to add more
                  <div className="activity-results-container">
                    {/* Back to add button */}
                    <div className="mb-4">
                      <button
                        onClick={() => setShowActivityResults(false)}
                        className="text-sm text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 flex items-center gap-1"
                      >
                        ← Add New Activity
                      </button>
                    </div>

                    {/* Display activity cards */}
                    <ActivityCardsDisplay 
                      activities={activities}
                      onEdit={async (activity) => {
                        try {
                          await updateActivity({ 
                            id: activity.id, 
                            data: {
                              activity_type: activity.activity_type,
                              position_description: activity.position_description,
                              organization_name: activity.organization_name,
                              activity_description: activity.activity_description,
                              participation_grades: activity.participation_grades,
                              timing_of_participation: activity.timing_of_participation,
                              hours_per_week: activity.hours_per_week,
                              weeks_per_year: activity.weeks_per_year,
                              intend_to_participate_in_college: activity.intend_to_participate_in_college
                            }
                          });
                        } catch (error) {
                          console.error('Failed to update activity:', error);
                        }
                      }}
                      onDelete={async (activityId) => {
                        try {
                          await deleteActivity(activityId);
                        } catch (error) {
                          console.error('Failed to delete activity:', error);
                        }
                      }}
                      onAnalyze={async (description: string) => {
                        try {
                          console.log('Starting activity analysis for:', description);
                          const result = await analyzeActivity({ description });
                          console.log('Analysis result:', result);
                          
                          // Automatically create the activity after analysis
                          const createdActivity = await createActivity({
                            activity_type: result.activity.activity_type,
                            position_description: result.activity.position_description,
                            organization_name: result.activity.organization_name,
                            activity_description: result.activity.activity_description,
                            participation_grades: result.activity.participation_grades,
                            timing_of_participation: result.activity.timing_of_participation,
                            hours_per_week: result.activity.hours_per_week,
                            weeks_per_year: result.activity.weeks_per_year,
                            intend_to_participate_in_college: result.activity.intend_to_participate_in_college,
                            raw_description: description
                          });
                          console.log('Activity created successfully:', createdActivity);
                        } catch (error) {
                          console.error('Failed to analyze and create activity:', error);
                          throw error;
                        }
                      }}
                      onReorder={async (reorderedActivities) => {
                        try {
                          await reorderActivities(reorderedActivities);
                        } catch (error) {
                          console.error('Failed to reorder activities:', error);
                        }
                      }}
                      isAnalyzing={isAnalyzing}
                    />
                    
                    {/* Display missing information prompts if any */}
                    {activityMissingInfo && activityMissingInfo.length > 0 && (
                      <div className="mt-6">
                        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                          <h5 className="font-semibold text-purple-900 dark:text-purple-100 mb-3">
                            💡 Suggestions to improve your activities:
                          </h5>
                          <ul className="space-y-2 text-sm text-purple-800 dark:text-purple-200">
                            {activityMissingInfo.map((suggestion, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <span className="text-purple-600 dark:text-purple-400 font-bold flex-shrink-0 mt-0.5">•</span>
                                {suggestion}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Career */}
          <div className="settings-card dark:bg-gray-800 dark:border-gray-700">
            <div 
              className="settings-card-header" 
              onClick={() => toggleSection('career')}
            >
              <div>
                <h3 className="settings-card-title dark:text-gray-100">Career</h3>
                <p className="settings-card-description dark:text-gray-400">Work experience, internships, and professional development</p>
              </div>
              <IoChevronDown 
                className={`settings-chevron dark:text-gray-400 ${expandedSections['career'] ? 'expanded' : ''}`}
              />
            </div>
            {expandedSections['career'] && (
              <div className="settings-card-content">
                <CareerModule />
              </div>
            )}
          </div>

          {/* Preferences & Interests */}
          <div className="settings-card dark:bg-gray-800 dark:border-gray-700">
            <div 
              className="settings-card-header" 
              onClick={() => toggleSection('preferences')}
            >
              <div>
                <h3 className="settings-card-title dark:text-gray-100">Preferences & Interests</h3>
                <p className="settings-card-description dark:text-gray-400">Define your academic interests, intended majors, and career goals</p>
              </div>
              <IoChevronDown 
                className={`settings-chevron dark:text-gray-400 ${expandedSections['preferences'] ? 'expanded' : ''}`}
              />
            </div>
            {expandedSections['preferences'] && (
              <div className="settings-card-content">
                <div className="settings-form-section">
                  <label className="settings-label">Intended Major (Primary)</label>
                  <select className="settings-input">
                    <option>Computer Science</option>
                    <option>Biology</option>
                    <option>Engineering</option>
                    <option>Business</option>
                    <option>Psychology</option>
                    <option>Undecided</option>
                  </select>
                </div>
                <div className="settings-form-section">
                  <label className="settings-label">Intended Major (Secondary)</label>
                  <select className="settings-input">
                    <option>Mathematics</option>
                    <option>Data Science</option>
                    <option>None</option>
                  </select>
                </div>
                <div className="settings-form-section">
                  <label className="settings-label">Career Interests</label>
                  <textarea 
                    rows={3} 
                    placeholder="Describe your career goals and interests..." 
                    className="settings-input"
                    defaultValue="Software development, artificial intelligence research, and entrepreneurship in tech startups."
                  />
                </div>
                <div className="settings-form-section">
                  <label className="settings-label">Academic Interests (Select all that apply)</label>
                  <div className="settings-checkbox-grid">
                    <label className="settings-checkbox-label">
                      <input type="checkbox" defaultChecked className="settings-checkbox" />
                      <span>STEM</span>
                    </label>
                    <label className="settings-checkbox-label">
                      <input type="checkbox" className="settings-checkbox" />
                      <span>Humanities</span>
                    </label>
                    <label className="settings-checkbox-label">
                      <input type="checkbox" defaultChecked className="settings-checkbox" />
                      <span>Social Sciences</span>
                    </label>
                    <label className="settings-checkbox-label">
                      <input type="checkbox" className="settings-checkbox" />
                      <span>Arts</span>
                    </label>
                    <label className="settings-checkbox-label">
                      <input type="checkbox" defaultChecked className="settings-checkbox" />
                      <span>Business</span>
                    </label>
                    <label className="settings-checkbox-label">
                      <input type="checkbox" className="settings-checkbox" />
                      <span>Pre-Med</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>









            </>
          )}
        </div>
      </div>

      {/* Common App Integration Modal */}
      <CommonAppModal
        isOpen={showCommonAppModal}
        onClose={() => setShowCommonAppModal(false)}
        onImportComplete={(importedData) => {
          console.log('Import completed:', importedData);
          setShowCommonAppModal(false);
          // Optionally refresh profile data here
          window.location.reload();
        }}
      />


    </div>
  );
};

export default Settings;

